/**
 * Pricing Calculator Types
 *
 * Types for the native pricing calculator form that replaces the Retool embedded calculator.
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx
 */

export enum PlanType {
  STANDARD = 'standard',
  /**
   * Sales renamed this plan from "Mosaic Lite" to "Base" in August 2026
   * (GLX-1931). The serialized value stays `mosaic_lite` on purpose: it is the
   * `pt` query parameter in shared quote links, so changing it would break
   * every link a rep has already sent out.
   */
  BASE = 'mosaic_lite',
  MOSAIC_ENTERPRISE = 'mosaic_enterprise',
}

/**
 * Customer-facing plan names. Anything a user reads must go through here rather
 * than rendering the enum value, which is a serialization format and still says
 * `mosaic_lite` for the Base plan.
 */
const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  [PlanType.STANDARD]: 'Standard',
  [PlanType.BASE]: 'Base',
  [PlanType.MOSAIC_ENTERPRISE]: 'Mosaic Enterprise',
};

/** Display name for a plan. Falls back to Standard, which is the form default. */
export const getPlanTypeLabel = (planType?: PlanType): string =>
  (planType && PLAN_TYPE_LABELS[planType]) || PLAN_TYPE_LABELS[PlanType.STANDARD];

/**
 * Dedicated-tenancy options on top of the shared default.
 * - SHARED: standard shared-tenant infrastructure (no extra fee).
 * - SINGLE: customer-dedicated environment; includes Isolated DB + Isolated AI.
 * - ENTERPRISE: same feature set as SINGLE at 60% of the Single Tenant fee.
 */
export enum TenantType {
  SHARED = 'shared',
  SINGLE = 'single',
  ENTERPRISE = 'enterprise',
}

/** Enterprise Tenant is priced at 60% of the Single Tenant fee (GLX-1719). */
export const ENTERPRISE_TENANT_MULTIPLIER = 0.6;

/**
 * True when the customer is on a dedicated-tenant environment (Single or
 * Enterprise). Used to gate features that ship as part of the dedicated tier
 * (Isolated DB, Isolated AI) and to drive integration-fee multipliers.
 */
export const isDedicatedTenant = (tenantType: TenantType): boolean =>
  tenantType !== TenantType.SHARED;

export interface PricingCalculatorFormValues {
  // Base Information
  companyName: string;
  subscriptionDuration: number; // in months
  isRenewal: boolean;
  isItCustomer: boolean;

  // Core Product
  planType: PlanType;
  numberOfMembers: number;
  pricePerMember: number;
  discountOnMemberPrice: number; // percentage
  numberOfInternalContractors: number;
  numberOfGuests: number;
  numberOfExternalContractors: number;
  platformFee: number;
  discountOnPlatformFee: number; // percentage
  numberOfOnboardingGroups: number;
  onboardingFee: number;
  discountOnOnboardingFee: number; // percentage
  additionalEnterpriseSupportHours: number; // Additional support hours for Enterprise plan ($200/hour)

  // Server Upgrades
  directDatabaseIntegration: boolean;
  discountOnDirectDatabase: number; // percentage
  isolatedDatabase: boolean;
  discountOnIsolatedDatabase: number; // percentage
  isolatedDatabaseMigration: boolean; // 1-time migration fee
  tenantType: TenantType;
  discountOnTenant: number; // percentage — applies to whichever dedicated tenant tier is selected
  isolatedAi: boolean;
  securityPackageSso: boolean;
  discountOnSecurityPackageSso: number; // percentage

  // Gov Cloud
  govCloud: boolean;
  govCloudFee: number;
  discountOnGovCloudFee: number; // percentage

  // Add-ons
  aiForecasting: boolean;
  discountOnAiForecasting: number; // percentage
  nonPeopleResources: boolean;
  discountOnNonPeopleResources: number; // percentage
  profileBuilding: boolean;
  discountOnProfileBuilding: number; // percentage
  skillsTagging: boolean;
  discountOnSkillsTagging: number; // percentage
  solutionsSupport: boolean;
  solutionsSupportHours: number; // Number of hours in 5-hour increments
  enableWhiteLabel: boolean;

  // Integrations
  selectedIntegrations: string[];
  bakIntegrationFetchTimes: number; // Total bak integration fetch times per month (1, 2, or 3)
  enable4xDataTransfer: boolean; // Enables 4x data transfer with 1.3x multiplier on integration recurring fees
  integrationMigration: boolean; // 50% discount on setup fee if checked
  discountOnIntegrationSetup: number; // percentage discount on setup fees
  discountOnIntegrationRecurring: number; // percentage discount on recurring fees
}

export interface PricingCalculatorSummary {
  // Plan Details
  originalPricePerMember: number;
  discountedPricePerMember: number;
  totalMemberCostMonthly: number;
  totalMemberCostYearly: number;

  // Additional Costs
  internalContractorsTotal: number;
  guestsTotal: number;
  externalContractorsTotal: number;

  // Platform Fee
  platformFeeMonthly: number;
  platformFeeAfterDiscountMonthly: number;
  platformFeeYearly: number;
  hoursIncluded: number;

  // Enterprise Support
  additionalEnterpriseSupportHours: number;
  additionalEnterpriseSupportCost: number;

  // One-time Fees
  onboardingFee: number;
  onboardingFeeAfterDiscount: number;

  // Add-ons Status
  isolatedDatabase: boolean;
  directDatabase: boolean;
  directDatabaseFeeBeforeDiscount: number; // Before discount
  directDatabaseFee: number; // After discount
  isMigration: boolean;
  oneTimeMaintenanceFee: number;
  isolatedDatabaseFeeYearlyBeforeDiscount: number; // Before discount
  isolatedDatabaseFeeYearly: number; // After discount
  tenantType: TenantType;
  tenantFeeBeforeDiscount: number; // Before discount
  tenantFee: number; // After discount
  isolatedAi: boolean;
  isolatedAiFee: number;
  securityPackageSso: boolean;
  ssoTotalYearlyBeforeDiscount: number; // Before discount
  ssoTotalYearly: number; // After discount
  govCloud: boolean;
  govCloudFee: number;
  govCloudFeeAfterDiscount: number;
  aiForecasting: boolean;
  aiForecastingFee: number;
  aiForecastingFeeAfterDiscount: number;
  nonPeopleResources: boolean;
  nonPeopleResourcesFee: number;
  nonPeopleResourcesFeeAfterDiscount: number;
  profileBuilding: boolean;
  profileBuildingFee: number;
  profileBuildingFeeAfterDiscount: number;
  skillsTagging: boolean;
  skillsTaggingFee: number;
  skillsTaggingFeeAfterDiscount: number;
  solutionsSupport: boolean;
  solutionsSupportHours: number;
  solutionsSupportFee: number;
  enableWhiteLabel: boolean;
  whiteLabelAdditionalCost: number;

  // Integration
  totalBakIntegrationFetchTimes: number;
  additionalBakIntegrationFetchingFee: number;
  integrationSetupFee: number; // One-time setup fee for all integrations
  integrationSetupFeeAfterDiscount: number; // After discount
  integrationRecurringFee: number; // Annual recurring fee for all integrations
  integrationRecurringFeeAfterDiscount: number; // After discount

  // Totals
  oneTimeFeeSubtotal: number;
  recurringFeeSubtotal: number;
  totalCost: number;
}

/**
 * Calculate seat price per month based on user count with tiered pricing
 *
 * Base Plan:
 * - Fixed price: $9.99 per user per month (regardless of user count)
 *
 * Mosaic Enterprise Plan Multipliers:
 * - Price per user per month: 1.5x the original calculated price
 */
export const calculateSeatPricePerMonth = (userCount: number, planType?: PlanType): number => {
  // Base has fixed pricing regardless of user count
  if (planType === PlanType.BASE) {
    return 9.99;
  }

  let basePrice: number;
  if (userCount <= 249) basePrice = 19.99;
  else if (userCount <= 499) basePrice = 14.99;
  else if (userCount <= 999) basePrice = 14.99;
  else if (userCount <= 4999) basePrice = 12.99;
  else basePrice = 9.99; // 5000+

  // Apply Mosaic Enterprise Plan multiplier
  if (planType === PlanType.MOSAIC_ENTERPRISE) {
    return Math.round(basePrice * 1.5 * 100) / 100;
  }

  return basePrice;
};

/**
 * Round Platform Fee "Hours included" up to a clean increment (GLX-1745).
 * - hours < 200 → round UP to nearest 5
 * - hours ≥ 200 → round UP to nearest 10
 * Zero and negative inputs pass through as 0.
 *
 * Since GLX-1931 the published tier hours are already clean increments, so this
 * only matters for values derived from them — chiefly the IT-Customer modifier,
 * which halves the published hours and lands off-increment.
 */
export const roundHoursUp = (hours: number): number => {
  if (hours <= 0) return 0;
  const increment = hours < 200 ? 5 : 10;
  return Math.ceil(hours / increment) * increment;
};

/** One row of the published platform-fee scale. `maxUsers` is inclusive. */
export interface PlatformFeeTier {
  /** Inclusive upper bound of the tier, in members. */
  maxUsers: number;
  /** Annual Standard-plan platform fee, in dollars. */
  standardFee: number;
  /** Support hours included in the Standard-plan fee. */
  standardHours: number;
  /** Annual Base-plan platform fee, in dollars (16+ members). */
  baseFee: number;
  /** Support hours included in the Base-plan fee (16+ members). */
  baseHours: number;
  /**
   * Annual Mosaic Enterprise platform fee, in dollars, or `null` for tiers
   * entirely below the 200-member floor where the plan is not sold — matching
   * the blank cells in the sheet.
   *
   * This is published rather than computed as `standardFee × 2` because the
   * sheet doubles *before* rounding to whole dollars: at ≤201 members it lists
   * $19,305 (9,750 × 1.98) where doubling the rounded $9,653 would give
   * $19,306. Of the 22 tiers where Enterprise is sold, 17 differ from
   * `standardFee × 2` by $1 that way. Included hours *are* exactly 2× the
   * Standard hours on every row, so those stay derived.
   */
  enterpriseFee: number | null;
}

/**
 * Published platform fee and included hours by user tier (GLX-1931).
 *
 * Source of truth is the Sales-owned "Pricing and hours" sheet, revision
 * 2026-08-26, "condensed view" tab: column I → `standardFee`, column N →
 * `standardHours`, column P → `enterpriseFee`, column S → `baseFee`, column U →
 * `baseHours`. Column Q (Enterprise hours) is exactly 2× column N on every
 * published row, so it is derived rather than transcribed.
 *
 * Before GLX-1931 these numbers were re-derived in code from a $3,900 base price
 * and a stack of plan multipliers. That derivation had drifted from the sheet —
 * it repeated a tier's fee at the 502- and 1,002-member increment changes, so
 * every tier above 501 members was one step behind — and it could not express
 * the hand-set Base hours at all. The sheet is now transcribed verbatim so a
 * quote built in Galaxy and one built in the sheet cannot disagree.
 *
 * Not covered here:
 * - Base plan at or below {@link BASE_PLAN_FLAT_FEE_MAX_USERS} members → flat
 *   {@link BASE_PLAN_FLAT_FEE} with no included hours.
 * - Mosaic Enterprise below {@link MOSAIC_ENTERPRISE_MIN_USERS} members → not
 *   sold, so `enterpriseFee` is `null` for tiers entirely under that floor.
 * - Above the last row → {@link extrapolatePlatformFeeTier}.
 *
 * Rows must stay ordered by ascending `maxUsers`; {@link resolvePlatformFeeTier}
 * relies on it. `pricingCalculator.test.ts` re-derives all three fee columns from
 * the sheet's own arithmetic and holds an independent transcription of the hours
 * columns, so a mistyped digit fails the build.
 */
export const PLATFORM_FEE_TIER_TABLE: readonly PlatformFeeTier[] = [
  {
    maxUsers: 25,
    standardFee: 1999,
    standardHours: 10,
    baseFee: 1500,
    baseHours: 8,
    enterpriseFee: null,
  },
  {
    maxUsers: 51,
    standardFee: 3432,
    standardHours: 20,
    baseFee: 2574,
    baseHours: 15,
    enterpriseFee: null,
  },
  {
    maxUsers: 101,
    standardFee: 5148,
    standardHours: 35,
    baseFee: 3861,
    baseHours: 20,
    enterpriseFee: null,
  },
  {
    maxUsers: 151,
    standardFee: 7722,
    standardHours: 50,
    baseFee: 5148,
    baseHours: 25,
    enterpriseFee: null,
  },
  {
    maxUsers: 201,
    standardFee: 9653,
    standardHours: 65,
    baseFee: 6435,
    baseHours: 30,
    enterpriseFee: 19305,
  },
  {
    maxUsers: 251,
    standardFee: 11583,
    standardHours: 80,
    baseFee: 7722,
    baseHours: 40,
    enterpriseFee: 23166,
  },
  {
    maxUsers: 301,
    standardFee: 13514,
    standardHours: 90,
    baseFee: 9009,
    baseHours: 45,
    enterpriseFee: 27027,
  },
  {
    maxUsers: 351,
    standardFee: 15444,
    standardHours: 105,
    baseFee: 10296,
    baseHours: 50,
    enterpriseFee: 30888,
  },
  {
    maxUsers: 401,
    standardFee: 17375,
    standardHours: 120,
    baseFee: 11583,
    baseHours: 60,
    enterpriseFee: 34749,
  },
  {
    maxUsers: 451,
    standardFee: 19305,
    standardHours: 130,
    baseFee: 12870,
    baseHours: 65,
    enterpriseFee: 38610,
  },
  {
    maxUsers: 501,
    standardFee: 21236,
    standardHours: 145,
    baseFee: 14157,
    baseHours: 70,
    enterpriseFee: 42471,
  },
  {
    maxUsers: 601,
    standardFee: 24131,
    standardHours: 160,
    baseFee: 16088,
    baseHours: 80,
    enterpriseFee: 48263,
  },
  {
    maxUsers: 701,
    standardFee: 27027,
    standardHours: 185,
    baseFee: 18018,
    baseHours: 90,
    enterpriseFee: 54054,
  },
  {
    maxUsers: 801,
    standardFee: 29923,
    standardHours: 200,
    baseFee: 19949,
    baseHours: 100,
    enterpriseFee: 59846,
  },
  {
    maxUsers: 901,
    standardFee: 32819,
    standardHours: 225,
    baseFee: 21879,
    baseHours: 115,
    enterpriseFee: 65637,
  },
  {
    maxUsers: 1001,
    standardFee: 35714,
    standardHours: 240,
    baseFee: 23810,
    baseHours: 120,
    enterpriseFee: 71429,
  },
  {
    maxUsers: 1251,
    standardFee: 39575,
    standardHours: 270,
    baseFee: 26384,
    baseHours: 135,
    enterpriseFee: 79151,
  },
  {
    maxUsers: 1501,
    standardFee: 43436,
    standardHours: 295,
    baseFee: 28958,
    baseHours: 150,
    enterpriseFee: 86873,
  },
  {
    maxUsers: 1751,
    standardFee: 47297,
    standardHours: 320,
    baseFee: 31532,
    baseHours: 160,
    enterpriseFee: 94595,
  },
  {
    maxUsers: 2001,
    standardFee: 51158,
    standardHours: 345,
    baseFee: 34106,
    baseHours: 170,
    enterpriseFee: 102317,
  },
  {
    maxUsers: 2251,
    standardFee: 55019,
    standardHours: 370,
    baseFee: 36680,
    baseHours: 185,
    enterpriseFee: 110039,
  },
  {
    maxUsers: 2501,
    standardFee: 58880,
    standardHours: 400,
    baseFee: 39254,
    baseHours: 200,
    enterpriseFee: 117761,
  },
  {
    maxUsers: 2751,
    standardFee: 62741,
    standardHours: 425,
    baseFee: 41828,
    baseHours: 215,
    enterpriseFee: 125483,
  },
  {
    maxUsers: 3001,
    standardFee: 66602,
    standardHours: 450,
    baseFee: 44402,
    baseHours: 225,
    enterpriseFee: 133205,
  },
  {
    maxUsers: 3251,
    standardFee: 70463,
    standardHours: 480,
    baseFee: 46976,
    baseHours: 240,
    enterpriseFee: 140927,
  },
  {
    maxUsers: 3501,
    standardFee: 74324,
    standardHours: 505,
    baseFee: 49550,
    baseHours: 250,
    enterpriseFee: 148649,
  },
];

const LAST_PUBLISHED_TIER = PLATFORM_FEE_TIER_TABLE[PLATFORM_FEE_TIER_TABLE.length - 1];

/**
 * The first member count that falls into a published tier. Tier 0 starts at 1;
 * every other tier starts one member past the previous tier's bound.
 *
 * Total by construction: an index past the end of the table returns one member
 * past the last published tier, which is what a "everything above this" row
 * wants, and an index at or below 0 returns 1. UI that renders a truncated view
 * of the scale can therefore ask for its overflow row without bounds-checking.
 */
export const getPlatformFeeTierMinUsers = (tierIndex: number): number => {
  if (tierIndex <= 0) return 1;

  const clampedIndex = Math.min(tierIndex, PLATFORM_FEE_TIER_TABLE.length);
  return PLATFORM_FEE_TIER_TABLE[clampedIndex - 1].maxUsers + 1;
};

/** The Base plan is a flat fee with no included hours at or below this member count. */
export const BASE_PLAN_FLAT_FEE_MAX_USERS = 15;
/** The flat Base-plan platform fee for the smallest teams. */
export const BASE_PLAN_FLAT_FEE = 999;

/** Mosaic Enterprise is not sold below this member count. */
export const MOSAIC_ENTERPRISE_MIN_USERS = 200;
/**
 * Enterprise includes exactly double the Standard hours, and doubles the
 * *unrounded* Standard share when extrapolating past the published table.
 * It does NOT double the published fee — see {@link PlatformFeeTier.enterpriseFee}.
 */
const MOSAIC_ENTERPRISE_MULTIPLIER = 2;

/**
 * Extrapolation rules for member counts above the last published tier.
 *
 * The sheet's own arithmetic simply continues: one tier per 250 members, its
 * list price growing by a flat $3,900 per tier, with the Standard, Base and
 * Enterprise fees taken as 90%, 60% and 180% of that list price.
 *
 * Those are the shares the sheet settles into: the 60% Base share reproduces
 * every published row from 26 members up, and the 90% Standard share reproduces
 * every row from 152 members up. Only the three smallest tiers sit outside it —
 * tiers 2 and 3 are struck at 80%, and tier 1 is hand-set on both columns — and
 * none of them is anywhere near the extrapolated range. Enterprise is 180%
 * rather than "double the Standard fee" for the same reason it is a published
 * column: the sheet doubles before rounding.
 * `pricingCalculator.test.ts` pins the exact share per row.
 *
 * Hours keep following the pre-GLX-1931 growth curve, halved for Base.
 *
 * Evaluated at the last published tier these rules reproduce that row exactly,
 * so there is no jump at the 3,501-member boundary (asserted in
 * `pricingCalculator.test.ts`).
 */
const USERS_PER_TIER_ABOVE_TABLE = 250;
const SHEET_LIST_PRICE_AT_LAST_TIER = 75075;
const SHEET_LIST_PRICE_INCREMENT_PER_TIER = 3900;
const SHEET_LIST_PRICE_MARKUP = 1.1;
const STANDARD_FEE_SHARE_OF_LIST = 0.9;
const BASE_FEE_SHARE_OF_LIST = 0.6;
const STANDARD_HOURS_SHARE_OF_BASE = 0.8;
const HOURS_INCREMENT = 5;

/** Base support hours at the last published tier, and growth per 250 members above it. */
const BASE_HOURS_AT_LAST_TIER = 630;
const BASE_HOURS_PER_BLOCK = 32;
const BASE_HOURS_DIMINISHING_FACTOR = 0.5;

const roundToNearestHoursIncrement = (hours: number): number =>
  Math.round(hours / HOURS_INCREMENT) * HOURS_INCREMENT;

/**
 * Base support hours for member counts above the published table: 630 hours at
 * 3,501 members, plus 32 hours per whole 250-member block, plus a diminishing
 * square-root component, rounded to the nearest 5.
 */
const calculateBaseHoursAboveTable = (userCount: number): number => {
  const usersAboveTable = Math.max(0, userCount - LAST_PUBLISHED_TIER.maxUsers);
  const linearGrowth =
    Math.floor(usersAboveTable / USERS_PER_TIER_ABOVE_TABLE) * BASE_HOURS_PER_BLOCK;
  const diminishingGrowth = Math.sqrt(usersAboveTable) * BASE_HOURS_DIMINISHING_FACTOR;

  return roundToNearestHoursIncrement(BASE_HOURS_AT_LAST_TIER + linearGrowth + diminishingGrowth);
};

/** Synthesize a tier row for member counts past the end of the published table. */
const extrapolatePlatformFeeTier = (userCount: number): PlatformFeeTier => {
  const tiersAboveTable = Math.ceil(
    Math.max(0, userCount - LAST_PUBLISHED_TIER.maxUsers) / USERS_PER_TIER_ABOVE_TABLE,
  );
  const listPrice =
    (SHEET_LIST_PRICE_AT_LAST_TIER + SHEET_LIST_PRICE_INCREMENT_PER_TIER * tiersAboveTable) *
    SHEET_LIST_PRICE_MARKUP;
  const standardHours = roundToNearestHoursIncrement(
    calculateBaseHoursAboveTable(userCount) * STANDARD_HOURS_SHARE_OF_BASE,
  );

  return {
    maxUsers: LAST_PUBLISHED_TIER.maxUsers + tiersAboveTable * USERS_PER_TIER_ABOVE_TABLE,
    standardFee: Math.round(listPrice * STANDARD_FEE_SHARE_OF_LIST),
    standardHours,
    baseFee: Math.round(listPrice * BASE_FEE_SHARE_OF_LIST),
    // Base runs at half the Standard entitlement, floored to a clean increment.
    // Flooring (rather than rounding to nearest) reproduces 20 of the 26
    // published rows and, crucially, the last one — so the handoff from table to
    // extrapolation at 3,501 members is seamless.
    baseHours: Math.floor(standardHours / 2 / HOURS_INCREMENT) * HOURS_INCREMENT,
    // Double the *unrounded* Standard share, matching how the sheet strikes its
    // Enterprise column. Rounding first and then doubling would drift by $1.
    enterpriseFee: Math.round(
      listPrice * STANDARD_FEE_SHARE_OF_LIST * MOSAIC_ENTERPRISE_MULTIPLIER,
    ),
  };
};

/**
 * The tier a member count falls into. Counts at or below the first row's bound —
 * including 0, which a team whose member counts failed to load can produce —
 * resolve to that first row.
 */
const resolvePlatformFeeTier = (userCount: number): PlatformFeeTier =>
  PLATFORM_FEE_TIER_TABLE.find((tier) => userCount <= tier.maxUsers) ??
  extrapolatePlatformFeeTier(userCount);

type PlatformFeeResult = { platformFee: number; hoursIncluded: number };

/**
 * Annual platform fee and the support hours it includes, for a member count and
 * plan (GLX-1931).
 *
 * Every plan reads its fee and hours straight out of
 * {@link PLATFORM_FEE_TIER_TABLE}; see that table for provenance. On top of it:
 * - Base at ≤15 members is a flat $999 with no included hours.
 * - Mosaic Enterprise takes its own published fee column and 2× the Standard
 *   hours, and returns a zero fee below 200 members because the plan is not sold
 *   at that size.
 * - Omitting `planType` is treated as Standard.
 *
 * The IT-Customer 50% modifier is applied by the caller, not here — see
 * `computePricingSummary`.
 */
export const calculatePlatformFee = (userCount: number, planType?: PlanType): PlatformFeeResult => {
  if (planType === PlanType.BASE && userCount <= BASE_PLAN_FLAT_FEE_MAX_USERS) {
    return { platformFee: BASE_PLAN_FLAT_FEE, hoursIncluded: 0 };
  }

  const tier = resolvePlatformFeeTier(userCount);

  if (planType === PlanType.BASE) {
    return { platformFee: tier.baseFee, hoursIncluded: tier.baseHours };
  }

  if (planType === PlanType.MOSAIC_ENTERPRISE) {
    // Not sold below 200 members. Report it as no fee so the quote drops the
    // line item entirely rather than silently charging a Standard price.
    if (userCount < MOSAIC_ENTERPRISE_MIN_USERS) {
      return { platformFee: 0, hoursIncluded: 0 };
    }

    return {
      // `enterpriseFee` is only null for tiers wholly under the 200-member
      // floor, which the guard above has already returned on.
      platformFee: tier.enterpriseFee ?? 0,
      hoursIncluded: tier.standardHours * MOSAIC_ENTERPRISE_MULTIPLIER,
    };
  }

  return { platformFee: tier.standardFee, hoursIncluded: tier.standardHours };
};

/**
 * SSO fee per user per month, by inclusive user-count band.
 * Bands are contiguous over 1-14999; 15000+ uses SSO_FEE_PER_USER_ABOVE_BANDS and
 * non-positive counts use SSO_FEE_PER_USER_NON_POSITIVE.
 */
const SSO_FEE_BANDS: ReadonlyArray<{ minUsers: number; maxUsers: number; feePerUser: number }> = [
  { minUsers: 1, maxUsers: 25, feePerUser: 0.5 },
  { minUsers: 26, maxUsers: 49, feePerUser: 0.5 },
  { minUsers: 50, maxUsers: 99, feePerUser: 0.4 },
  { minUsers: 100, maxUsers: 249, feePerUser: 0.38 },
  { minUsers: 250, maxUsers: 499, feePerUser: 0.3 },
  { minUsers: 500, maxUsers: 999, feePerUser: 0.25 },
  { minUsers: 1000, maxUsers: 1999, feePerUser: 0.23 },
  { minUsers: 2000, maxUsers: 2999, feePerUser: 0.21 },
  { minUsers: 3000, maxUsers: 3999, feePerUser: 0.18 },
  { minUsers: 4000, maxUsers: 4999, feePerUser: 0.17 },
  { minUsers: 5000, maxUsers: 5999, feePerUser: 0.15 },
  { minUsers: 6000, maxUsers: 6999, feePerUser: 0.14 },
  { minUsers: 7000, maxUsers: 7999, feePerUser: 0.12 },
  { minUsers: 8000, maxUsers: 8999, feePerUser: 0.11 },
  { minUsers: 9000, maxUsers: 9999, feePerUser: 0.1 },
  { minUsers: 10000, maxUsers: 10999, feePerUser: 0.09 },
  { minUsers: 11000, maxUsers: 11999, feePerUser: 0.08 },
  { minUsers: 12000, maxUsers: 12999, feePerUser: 0.07 },
  { minUsers: 13000, maxUsers: 13999, feePerUser: 0.07 },
  { minUsers: 14000, maxUsers: 14999, feePerUser: 0.06 },
];

const SSO_FEE_PER_USER_ABOVE_BANDS = 0.05; // 15000+
const SSO_FEE_PER_USER_NON_POSITIVE = 0.5;

/**
 * Calculate SSO fee per user per month based on user count
 */
export const calculateSsoFeePerUser = (userCount: number): number => {
  if (userCount <= 0) return SSO_FEE_PER_USER_NON_POSITIVE;

  const band = SSO_FEE_BANDS.find(
    ({ minUsers, maxUsers }) => userCount >= minUsers && userCount <= maxUsers,
  );

  return band ? band.feePerUser : SSO_FEE_PER_USER_ABOVE_BANDS;
};

/**
 * Calculate Isolated Database fee per month
 * Formula:
 * - Base fee: $100 per month
 * - Over 100 users: Additional $0.50 per user per month
 */
export const calculateIsolatedDatabaseFee = (userCount: number): number => {
  const BASE_FEE = 100;
  const COST_PER_USER_OVER_100 = 0.5;

  if (userCount <= 100) {
    return BASE_FEE;
  }

  const usersOver100 = userCount - 100;
  const additionalFee = usersOver100 * COST_PER_USER_OVER_100;

  return BASE_FEE + additionalFee;
};

/**
 * Calculate integration tier pricing per month based on number of integrations and user count
 *
 * User tier boundaries:
 * - Tier 1: 1-25 users (75% of base price)
 * - Tier 2: 26-51 users (base price)
 * - Tier 3: 52-101 users
 * - Tier 4: 102-151 users
 * - And so on...
 *
 * Base prices (for tier 2: 26-51 users) with 0.9 multiplier applied:
 * - Tier 1 (1 integration): $1,200 * 0.9 = $1,080
 * - Tier 2 (2 integrations): $3,900 * 0.9 = $3,510
 * - Tier 3 (3 integrations): $3,920 * 0.9 = $3,528
 * - Tier 4 (4 integrations): $6,675 * 0.9 = $6,007.50
 *
 * For tier 3+: Each tier adds 50% of base price
 */
export const calculateIntegrationTierPricing = (
  numberOfIntegrations: number,
  userCount: number,
): number => {
  if (numberOfIntegrations === 0) return 0;

  // Base prices for tier 2 (26-51 users) - old values * 0.9
  const BASE_PRICES: Record<number, number> = {
    1: 1200 * 0.9, // $1,080
    2: 3900 * 0.9, // $3,510
    3: 3920 * 0.9, // $3,528
    4: 6675 * 0.9, // $6,007.50
  };

  // Cap at tier 4 for more than 4 integrations
  const tier = Math.min(numberOfIntegrations, 4);
  const basePrice = BASE_PRICES[tier] || BASE_PRICES[4];

  // User Tier 1: 1-25 users (75% of base price)
  if (userCount <= 25) {
    return Math.round(basePrice * 0.75 * 100) / 100;
  }

  // User Tier 2: 26-51 users (base price)
  if (userCount <= 51) {
    return basePrice;
  }

  // User Tier 3+: 52+ users
  // Determine which tier based on boundaries (same as platform fee)
  let tierNumber: number;
  if (userCount <= 101) tierNumber = 3;
  else if (userCount <= 151) tierNumber = 4;
  else if (userCount <= 201) tierNumber = 5;
  else if (userCount <= 251) tierNumber = 6;
  else if (userCount <= 301) tierNumber = 7;
  else if (userCount <= 351) tierNumber = 8;
  else if (userCount <= 401) tierNumber = 9;
  else if (userCount <= 451) tierNumber = 10;
  else if (userCount <= 501) tierNumber = 11;
  else {
    // For users above 501, continue pattern with 50-user increments
    tierNumber = 11 + Math.ceil((userCount - 501) / 50);
  }

  // Each tier above tier 2 adds 50% of base price
  const tiersAbove2 = tierNumber - 2;
  const increment = basePrice * 0.5;
  const totalPrice = basePrice + increment * tiersAbove2;

  return Math.round(totalPrice * 100) / 100;
};

/**
 * Calculate Single Tenant fee based on user count and subscription duration
 *
 * User tier boundaries:
 * - Tier 1: 1-25 users
 * - Tier 2: 26-51 users
 * - Tier 3: 52-101 users
 * - Tier 4: 102-151 users
 * - Tier 5: 152-201 users
 * - And so on (50-user increments)
 *
 * Formula (linear scaling, mirrors the integration setup fee fix in GLX-1736):
 * - Base: $48,000 for tier 1 (12 months)
 * - Every tier above tier 1 adds a flat $2,400 (5% of the $48,000 base) —
 *   *not* compounding off the previous tier as the original formula did.
 *
 *   Tier 1 (≤25):     $48,000
 *   Tier 2 (≤51):     $50,400   (+$2,400)
 *   Tier 3 (≤101):    $52,800   (+$2,400 from tier 2; was $52,920 under compounding)
 *   Tier 4 (≤151):    $55,200   (was $55,566)
 *   Tier 11 (≤501):   $72,000   (was $78,187)
 *   Tier 37 (≤1801):  $134,400  (was ≈$278,000)
 *
 * - Fee is prorated based on subscription duration (12 months baseline).
 *
 * @param userCount - Number of users
 * @param subscriptionDuration - Subscription duration in months (default: 12)
 * @returns Total Single Tenant fee for the subscription period
 */
export const calculateSingleTenantFee = (
  userCount: number,
  subscriptionDuration: number = 12,
): number => {
  const BASE_PRICE = 48000; // Tier-1 annual base
  const INCREMENT_PER_TIER = 2400; // 5% of $48,000 base, added once per tier above tier 1
  const BASE_DURATION = 12;

  // Determine tier (50-user increments above tier 2)
  let tierNumber: number;
  if (userCount <= 25) tierNumber = 1;
  else if (userCount <= 51) tierNumber = 2;
  else if (userCount <= 101) tierNumber = 3;
  else if (userCount <= 151) tierNumber = 4;
  else if (userCount <= 201) tierNumber = 5;
  else if (userCount <= 251) tierNumber = 6;
  else if (userCount <= 301) tierNumber = 7;
  else if (userCount <= 351) tierNumber = 8;
  else if (userCount <= 401) tierNumber = 9;
  else if (userCount <= 451) tierNumber = 10;
  else if (userCount <= 501) tierNumber = 11;
  else tierNumber = 11 + Math.ceil((userCount - 501) / 50);

  const baseFeeFor12Months = BASE_PRICE + (tierNumber - 1) * INCREMENT_PER_TIER;

  return Math.round(((baseFeeFor12Months * subscriptionDuration) / BASE_DURATION) * 100) / 100;
};

/**
 * Calculate the dedicated tenant fee for the given tenant type (GLX-1719).
 * - SHARED returns 0 (no dedicated fee).
 * - SINGLE returns calculateSingleTenantFee().
 * - ENTERPRISE returns 60% of the Single Tenant fee, rounded to cents.
 */
export const calculateTenantFee = (
  tenantType: TenantType,
  userCount: number,
  subscriptionDuration: number = 12,
): number => {
  if (tenantType === TenantType.SHARED) return 0;
  const singleFee = calculateSingleTenantFee(userCount, subscriptionDuration);
  if (tenantType === TenantType.ENTERPRISE) {
    return Math.round(singleFee * ENTERPRISE_TENANT_MULTIPLIER * 100) / 100;
  }
  return singleFee;
};

/**
 * Calculate number of onboarding groups based on number of members.
 *
 * Reads the group capacity (MEMBERS_PER_ONBOARDING_GROUP) and remainder
 * threshold (ONBOARDING_GROUP_THRESHOLD) from PRICING_CONSTANTS so the math and
 * the UI tooltip share one source of truth (GLX-1748 — previously the capacity
 * was hardcoded to 200 here while the tooltip read the constant, so bumping the
 * constant alone would not have taken effect).
 *
 * Formula: floor(members / capacity), plus one more group if the remainder
 * meets the threshold. With the current 250-capacity / 150-threshold:
 * - 1–399 members = 1 group
 * - 400–649 members = 2 groups
 * - 650–899 members = 3 groups (e.g. 700 members = 3 groups)
 */
export const calculateOnboardingGroups = (numberOfMembers: number): number => {
  if (numberOfMembers <= 0) return 1;

  const capacity = PRICING_CONSTANTS.MEMBERS_PER_ONBOARDING_GROUP;
  const threshold = PRICING_CONSTANTS.ONBOARDING_GROUP_THRESHOLD;

  const groups =
    Math.floor(numberOfMembers / capacity) + (numberOfMembers % capacity >= threshold ? 1 : 0);

  return groups || 1;
};

/**
 * Onboarding fee per group (GLX-1748). Mosaic Enterprise Plan onboarding is
 * priced at $10,000/group; every other plan uses the $6,000/group standard rate.
 */
export const getOnboardingFeePerGroup = (planType: PlanType): number =>
  planType === PlanType.MOSAIC_ENTERPRISE
    ? PRICING_CONSTANTS.ONBOARDING_FEE_PER_GROUP_ENTERPRISE
    : PRICING_CONSTANTS.ONBOARDING_FEE_PER_GROUP;

/**
 * Calculate Direct Database Integration fee
 *
 * Formula: $50 per user, starting at $2,000 (40 users baseline) for 12 months
 * Price increases like tax brackets with 5% discount for every 10 users above 40
 * Fee is prorated based on subscription duration (12 months is the baseline)
 *
 * Pricing tiers (cumulative discounts for 12 months):
 * - 0-40 users: $2,000 flat (baseline)
 * - 41-50 users: $47.50 per user (5% discount)
 * - 51-60 users: $45 per user (10% discount)
 * - 61-70 users: $42.50 per user (15% discount)
 * - 71-80 users: $40 per user (20% discount)
 * - And so on... (5% discount increase per 10-user bracket)
 *
 * Examples (12 months):
 * - 40 users: $2,000 (baseline)
 * - 50 users: $2,000 + (10 × $50 × 0.95) = $2,000 + $475 = $2,475
 * - 60 users: $2,000 + (10 × $50 × 0.95) + (10 × $50 × 0.90) = $2,000 + $475 + $450 = $2,925
 * - 70 users: $2,000 + $475 + $450 + (10 × $50 × 0.85) = $2,000 + $475 + $450 + $425 = $3,350
 *
 * The 12-month base fee is enforced at $2,000 minimum before discount is applied.
 * Discount is then applied to this amount, and the result is prorated by subscription duration.
 *
 * @param userCount - Number of users
 * @param subscriptionDuration - Subscription duration in months (default: 12)
 * @param discountPercentage - Additional discount percentage to apply (default: 0)
 * @returns Total Direct Database fee prorated based on subscription duration ($2,000 minimum enforced before discount)
 */
export const calculateDirectDatabaseFee = (
  userCount: number,
  subscriptionDuration: number = 12,
  discountPercentage: number = 0,
): number => {
  const BASE_USERS = 40;
  const BASE_FEE = PRICING_CONSTANTS.DIRECT_DATABASE_MIN_FEE; // $2,000 for 12 months
  const COST_PER_USER = PRICING_CONSTANTS.DIRECT_DATABASE_COST_PER_USER; // $50 per user for 12 months
  const BRACKET_SIZE = 10; // Users per bracket
  const DISCOUNT_PER_BRACKET = 0.05; // 5% discount per bracket
  const BASE_DURATION = 12; // Base duration is 12 months

  // Calculate base fee for 12 months using bracket pricing
  let baseFeeFor12Months: number;

  // If user count is at or below baseline, use base fee
  if (userCount <= BASE_USERS) {
    baseFeeFor12Months = BASE_FEE;
  } else {
    // Calculate additional users above baseline
    const additionalUsers = userCount - BASE_USERS;

    // Calculate fee using tax bracket approach
    let additionalFee = 0;
    let remainingUsers = additionalUsers;
    let bracketIndex = 0;

    while (remainingUsers > 0) {
      // Users in current bracket (max 10)
      const usersInBracket = Math.min(remainingUsers, BRACKET_SIZE);

      // Discount for this bracket (5% for first bracket, 10% for second, etc.)
      // Formula: bracketIndex 0 = 5% discount (0.95 multiplier)
      //          bracketIndex 1 = 10% discount (0.90 multiplier)
      //          bracketIndex 2 = 15% discount (0.85 multiplier)
      // Cap the discount at 95% (multiplier at 0.05) so it never goes to zero or negative
      const discountMultiplier = Math.max(0.05, 1 - (bracketIndex + 1) * DISCOUNT_PER_BRACKET);

      // Add cost for this bracket
      additionalFee += usersInBracket * COST_PER_USER * discountMultiplier;

      // Move to next bracket
      remainingUsers -= usersInBracket;
      bracketIndex++;
    }

    baseFeeFor12Months = BASE_FEE + additionalFee;
  }

  // Ensure fee is at least the minimum before discount
  const feeBeforeDiscount = Math.max(BASE_FEE, baseFeeFor12Months);

  // Apply the form discount percentage
  const finalFeeFor12Months = feeBeforeDiscount * (1 - discountPercentage / 100);

  // Prorate based on actual subscription duration
  return Math.round(((finalFeeFor12Months * subscriptionDuration) / BASE_DURATION) * 100) / 100;
};

/**
 * Calculate Gov Cloud fee based on form values
 *
 * Gov Cloud fee = 20% of:
 * - Member costs (base user pricing)
 * - SSO costs (if enabled)
 * - Isolated database or dedicated tenant fee (if enabled)
 * - Integration fees (if any integrations selected)
 *
 * Formula:
 * - Gov Cloud fee = 0.2 * (memberCost + ssoTotalCost + databaseFee + integrationFee)
 * - Fee is calculated based on subscription duration
 * - NOTE: Gov Cloud does NOT include directDatabaseIntegration; the database surface
 *   is either isolatedDatabase or whichever dedicated tenant tier is selected.
 *
 * @returns Gov Cloud fee (20% of base costs) for the subscription period
 */
export const calculateGovCloudFee = (
  numberOfMembers: number,
  pricePerMember: number,
  discountOnMemberPrice: number,
  subscriptionDuration: number,
  securityPackageSso: boolean,
  isolatedDatabase: boolean,
  tenantType: TenantType,
  selectedIntegrationsCount: number,
): number => {
  // Calculate member cost
  const discountedPricePerMember = pricePerMember * (1 - discountOnMemberPrice / 100);
  const totalMemberCost = numberOfMembers * discountedPricePerMember * subscriptionDuration;

  // Calculate SSO cost (if enabled)
  const ssoFeePerUser = securityPackageSso ? calculateSsoFeePerUser(numberOfMembers) : 0;
  const ssoTotalCost = ssoFeePerUser * numberOfMembers * subscriptionDuration;

  // Calculate isolated database or dedicated tenant fee
  // NOTE: Gov Cloud only supports isolatedDatabase or a dedicated tenant (NOT directDatabaseIntegration)
  let databaseFee = 0;
  if (isDedicatedTenant(tenantType)) {
    databaseFee = calculateTenantFee(tenantType, numberOfMembers, subscriptionDuration);
  } else if (isolatedDatabase) {
    databaseFee = calculateIsolatedDatabaseFee(numberOfMembers) * subscriptionDuration;
  }

  // Calculate integration fee (calculateIntegrationTierPricing returns annual fee)
  const integrationFeeAnnual = calculateIntegrationTierPricing(
    selectedIntegrationsCount,
    numberOfMembers,
  );
  const integrationFeeTotalCost = (integrationFeeAnnual / 12) * subscriptionDuration;

  // Total Gov Cloud fee = 20% of all costs
  const baseCost = totalMemberCost + ssoTotalCost + databaseFee + integrationFeeTotalCost;
  return 0.2 * baseCost;
};

/**
 * Calculate Gov Cloud Platform Fee based on user count
 * Uses lookup table with Gov Cloud-specific user tiers
 *
 * @param userCount - Number of users
 * @returns Annual Gov Cloud Platform Fee
 */
export const calculateGovCloudPlatformFee = (userCount: number): number => {
  const tierKey = getGovCloudUserCountTierKey(userCount);
  return GOV_CLOUD_PLATFORM_FEE_TABLE[tierKey] || 0;
};

/**
 * Calculate AI Forecasting fee
 *
 * Formula: 35% × (Total Annual Member Fees + Total Annual Integration Fees) / 12 × duration
 * - Total Annual Member Fees = member price × number of members × 12 months
 * - Total Annual Integration Fees = annual integration tier fee (already annual)
 *
 * @param numberOfMembers - Number of members
 * @param pricePerMember - Price per member per month
 * @param discountOnMemberPrice - Discount percentage on member price
 * @param subscriptionDuration - Subscription duration in months
 * @param integrationFeeAnnual - Annual integration tier fee
 * @returns AI Forecasting fee for subscription period
 */
export const calculateAiForecastingFee = (
  numberOfMembers: number,
  pricePerMember: number,
  discountOnMemberPrice: number,
  subscriptionDuration: number,
  integrationFeeAnnual: number,
): number => {
  const discountedPricePerMember = pricePerMember * (1 - discountOnMemberPrice / 100);
  // Total Annual Member Fees = member price × number of members × 12 months
  const totalAnnualMemberFee = numberOfMembers * discountedPricePerMember * 12;
  // AI Forecasting Fee = 35% × (annualMember + annualIntegration) / 12 × duration
  const fee = ((0.35 * (totalAnnualMemberFee + integrationFeeAnnual)) / 12) * subscriptionDuration;
  return fee;
};

/**
 * Calculate Non-People Resources fee
 *
 * Formula: 50% of Discounted License Cost * number of users * subscription duration
 *
 * @param numberOfMembers - Number of members
 * @param pricePerMember - Price per member per month
 * @param discountOnMemberPrice - Discount percentage on member price
 * @param subscriptionDuration - Subscription duration in months
 * @returns Non-People Resources fee
 */
export const calculateNonPeopleResourcesFee = (
  numberOfMembers: number,
  pricePerMember: number,
  discountOnMemberPrice: number,
  subscriptionDuration: number,
): number => {
  const discountedPricePerMember = pricePerMember * (1 - discountOnMemberPrice / 100);
  const fee = 0.5 * discountedPricePerMember * numberOfMembers * subscriptionDuration;
  return fee;
};

/**
 * Calculate Profile Building fee
 *
 * Formula: $10 * number of users * subscription duration
 *
 * @param numberOfMembers - Number of members
 * @param subscriptionDuration - Subscription duration in months
 * @returns Profile Building fee for subscription period
 */
export const calculateProfileBuildingFee = (
  numberOfMembers: number,
  subscriptionDuration: number,
): number => {
  return 10 * numberOfMembers * subscriptionDuration;
};

/**
 * Calculate Skills Tagging fee
 *
 * Formula: $5 * number of users * subscription duration
 *
 * @param numberOfMembers - Number of members
 * @param subscriptionDuration - Subscription duration in months
 * @returns Skills Tagging fee for subscription period
 */
export const calculateSkillsTaggingFee = (
  numberOfMembers: number,
  subscriptionDuration: number,
): number => {
  return 5 * numberOfMembers * subscriptionDuration;
};

/**
 * Calculate Solutions Support fee
 *
 * Formula: hours * $200 (in 5-hour increments)
 *
 * @param hours - Number of hours (must be in 5-hour increments)
 * @returns Solutions Support fee
 */
export const calculateSolutionsSupportFee = (hours: number): number => {
  return hours * 200;
};

/**
 * Calculate White-Label additional cost
 *
 * Formula: 20% increase in per seat per month cost
 *
 * @param numberOfMembers - Number of members
 * @param pricePerMember - Price per member per month
 * @param discountOnMemberPrice - Discount percentage on member price
 * @param subscriptionDuration - Subscription duration in months
 * @returns White-Label additional cost
 */
export const calculateWhiteLabelAdditionalCost = (
  numberOfMembers: number,
  pricePerMember: number,
  discountOnMemberPrice: number,
  subscriptionDuration: number,
): number => {
  const discountedPricePerMember = pricePerMember * (1 - discountOnMemberPrice / 100);
  const additionalCost = 0.2 * discountedPricePerMember * numberOfMembers * subscriptionDuration;
  return additionalCost;
};

/**
 * Get user count tier key for integration pricing lookup (regular pricing)
 */
/** Tiers up to 501 users; null when the count falls outside this range. */
const getUserCountTierKeyUpTo501 = (userCount: number): string | null => {
  if (userCount <= 25) return '0-25';
  if (userCount <= 51) return '26-51';
  if (userCount <= 101) return '52-101';
  if (userCount <= 151) return '102-151';
  if (userCount <= 201) return '152-201';
  if (userCount <= 251) return '202-251';
  if (userCount <= 301) return '252-301';
  if (userCount <= 351) return '302-351';
  if (userCount <= 401) return '352-401';
  if (userCount <= 451) return '402-451';
  if (userCount <= 501) return '452-501';
  return null;
};

/** Tiers above 501 users, saturating at the highest tier. */
const getUserCountTierKeyAbove501 = (userCount: number): string => {
  if (userCount <= 601) return '502-601';
  if (userCount <= 701) return '602-701';
  if (userCount <= 801) return '702-801';
  if (userCount <= 901) return '802-901';
  if (userCount <= 1001) return '902-1001';
  if (userCount <= 1251) return '1002-1251';
  if (userCount <= 1501) return '1252-1501';
  if (userCount <= 1751) return '1502-1751';
  if (userCount <= 2001) return '1752-2001';
  if (userCount <= 2251) return '2002-2251';
  if (userCount <= 2501) return '2252-2501';
  if (userCount <= 2751) return '2502-2751';
  if (userCount <= 3001) return '2752-3001';
  if (userCount <= 3251) return '3002-3251';
  if (userCount <= 3501) return '3252-3501';
  // For users above 3501, use the highest tier
  return '3252-3501';
};

const getUserCountTierKey = (userCount: number): string =>
  getUserCountTierKeyUpTo501(userCount) ?? getUserCountTierKeyAbove501(userCount);

/**
 * Get user count tier key for Gov Cloud pricing lookup
 * Gov Cloud uses different tier boundaries than regular pricing
 *
 * Tiers: 49, 99, 149, 199, 249, 299, 349, 399, 449, 499, 599, 699, 799, 899, 999,
 *        1249, 1499, 1749, 1999, 2249, 2499, 2749, 2999, 3249, 3499
 */
/** Gov Cloud tiers up to 499 users; null when the count falls outside this range. */
const getGovCloudUserCountTierKeyUpTo499 = (userCount: number): string | null => {
  if (userCount <= 49) return '1-49';
  if (userCount <= 99) return '50-99';
  if (userCount <= 149) return '100-149';
  if (userCount <= 199) return '150-199';
  if (userCount <= 249) return '200-249';
  if (userCount <= 299) return '250-299';
  if (userCount <= 349) return '300-349';
  if (userCount <= 399) return '350-399';
  if (userCount <= 449) return '400-449';
  if (userCount <= 499) return '450-499';
  return null;
};

/** Gov Cloud tiers above 499 users, saturating at the highest tier. */
const getGovCloudUserCountTierKeyAbove499 = (userCount: number): string => {
  if (userCount <= 599) return '500-599';
  if (userCount <= 699) return '600-699';
  if (userCount <= 799) return '700-799';
  if (userCount <= 899) return '800-899';
  if (userCount <= 999) return '900-999';
  if (userCount <= 1249) return '1000-1249';
  if (userCount <= 1499) return '1250-1499';
  if (userCount <= 1749) return '1500-1749';
  if (userCount <= 1999) return '1750-1999';
  if (userCount <= 2249) return '2000-2249';
  if (userCount <= 2499) return '2250-2499';
  if (userCount <= 2749) return '2500-2749';
  if (userCount <= 2999) return '2750-2999';
  if (userCount <= 3249) return '3000-3249';
  if (userCount <= 3499) return '3250-3499';
  // For users above 3499, use the highest tier
  return '3250-3499';
};

const getGovCloudUserCountTierKey = (userCount: number): string =>
  getGovCloudUserCountTierKeyUpTo499(userCount) ?? getGovCloudUserCountTierKeyAbove499(userCount);

/**
 * Calculate integration setup fee for a single integration
 *
 * Formula:
 * - First-time integrations: Use tier-based pricing (supported by merge vs not supported)
 * - Existing integrations (linear scaling above 100 users, GLX-1736):
 *   - Regular price: $4K base, +$80 (2% of base) per additional 25 users
 *     (100: $4,000 | 125: $4,080 | 500: $5,280 | 1,800: $9,440)
 *   - Single tenant: $10K base, +$250 per additional 25 users
 *
 * @param integrationKey - Integration key (e.g., "open-api", "salesforce")
 * @param userCount - Number of users
 * @param isRenewal - Whether this is a renewal (setup fee is $0 if renewal)
 * @param isMigration - Whether this is a migration (50% discount on setup fee)
 * @param discountPercent - Additional discount percentage (0-100)
 * @param isSingleTenant - Whether this is a single tenant deployment (default: false)
 * @param isFirstTime - Whether this is a first-time integration (uses tier-based pricing, default: false)
 * @returns Setup fee for the integration
 */
export const calculateIntegrationSetupFee = (
  integrationKey: string,
  userCount: number,
  isRenewal: boolean,
  isMigration: boolean = false,
  discountPercent: number = 0,
  isSingleTenant: boolean = false,
  isFirstTime: boolean = false,
): number => {
  // If renewal, setup fee is $0
  if (isRenewal) return 0;

  const metadata = INTEGRATION_METADATA[integrationKey];
  if (!metadata) return 0; // Unknown integration

  const metadataValue = metadata.value;

  // Check for special override prices
  if (INTEGRATION_SPECIAL_SETUP_FEES[metadataValue] !== undefined) {
    let fee = INTEGRATION_SPECIAL_SETUP_FEES[metadataValue];

    // Apply migration discount
    if (isMigration && fee > 0) {
      fee = fee * PRICING_CONSTANTS.INTEGRATION_MIGRATION_DISCOUNT;
    }

    // Apply additional discount
    if (discountPercent > 0) {
      fee = fee * (1 - discountPercent / 100);
    }

    return Math.round(fee * 100) / 100;
  }

  let baseFee: number;

  // First-time integrations use tier-based pricing (supported by merge vs not)
  if (isFirstTime) {
    // Determine user count tier for setup fee
    let userTier: '0-499' | '500-999' | '1000+';
    if (userCount < 500) userTier = '0-499';
    else if (userCount < 1000) userTier = '500-999';
    else userTier = '1000+';

    // Get base setup fee based on merge support
    const feeTable = metadata.supported_by_merge_dev
      ? INTEGRATION_SETUP_FEE_TIERS.SUPPORTED_BY_MERGE
      : INTEGRATION_SETUP_FEE_TIERS.NOT_SUPPORTED_BY_MERGE;

    baseFee = feeTable[userTier];
  } else {
    // Existing integrations use new formula based on user count and single tenant status
    baseFee = calculateIntegrationSetupFeeByUserCount(userCount, isSingleTenant);
  }

  // Apply migration discount (50% off)
  if (isMigration) {
    baseFee = baseFee * PRICING_CONSTANTS.INTEGRATION_MIGRATION_DISCOUNT;
  }

  // Apply additional discount
  if (discountPercent > 0) {
    baseFee = baseFee * (1 - discountPercent / 100);
  }

  return Math.round(baseFee * 100) / 100;
};

/**
 * Calculate integration recurring annual fee for a single integration
 *
 * Formula based on requirements:
 * - For 'api-keys' (API + MCP): $2,400 base + $12.00/user/year above 100 users
 * - For others: Lookup from Regular_Fee or Gov_Cloud_Fee table by complexity
 * - Describe the fee: smallest tier discounted 25% from base price (up to 51 users);
 *   then each tier adds 50% of the base price on top of the previous tier
 * - If 4x data transfer: multiply by 1.3
 * - If single tenant: add 20% of original fee + $10,000
 * - If gov cloud: use Gov_Cloud_Fee table instead
 *
 * @param integrationKey - Integration key (e.g., "open-api", "salesforce")
 * @param userCount - Number of users
 * @param isGovCloud - Whether this is a gov cloud deployment
 * @param isSingleTenant - Whether this is a single tenant deployment
 * @param is4xDataTransfer - Whether 4x data transfer is enabled
 * @param discountPercent - Additional discount percentage (0-100)
 * @returns Recurring annual fee for the integration
 */
export const calculateIntegrationRecurringFee = (
  integrationKey: string,
  userCount: number,
  isGovCloud: boolean = false,
  isSingleTenant: boolean = false,
  is4xDataTransfer: boolean = false,
  discountPercent: number = 0,
): number => {
  const metadata = INTEGRATION_METADATA[integrationKey];
  if (!metadata) return 0; // Unknown integration

  let baseFee = 0;

  // Special case for 'api-keys' (API + MCP)
  if (metadata.value === 'api-keys') {
    // $2,400 base for first 100 users + $1.00/user/month ($12.00/user/year) for users above 100
    baseFee = 2400;
    if (userCount > 100) {
      baseFee += (userCount - 100) * 12;
    }
  } else {
    // Lookup from pricing table
    const complexity = metadata.complexity_group;

    if (isGovCloud) {
      // Use Gov Cloud table with Gov Cloud-specific tier keys
      const govCloudTierKey = getGovCloudUserCountTierKey(userCount);
      const tierPricing = GOV_CLOUD_INTEGRATION_FEE_TABLE[govCloudTierKey];
      if (tierPricing) {
        // Gov Cloud table supports complexity 1-5
        baseFee = tierPricing[complexity as ComplexityGroup] || 0;
      }
    } else {
      // Use regular table with regular tier keys
      const tierKey = getUserCountTierKey(userCount);
      const tierPricing = INTEGRATION_REGULAR_FEE_TABLE[tierKey];
      if (tierPricing) {
        // Regular table only supports complexity 1-4
        baseFee = tierPricing[complexity as ComplexityGroupUpToFour] || 0;
      }
    }
  }

  // Store original fee for single tenant calculation
  const originalFee = baseFee;

  // Apply 4x data transfer multiplier (30% increase)
  if (is4xDataTransfer) {
    baseFee = baseFee * PRICING_CONSTANTS.INTEGRATION_FETCH_COST_4X_MULTIPLIER;
  }

  // Apply single tenant modifier (20% of original + $10,000)
  // Open API (api-keys) is exempt from single tenant pricing bump
  if (isSingleTenant && metadata.value !== 'api-keys') {
    const singleTenantAdditional =
      originalFee * PRICING_CONSTANTS.INTEGRATION_SINGLE_TENANT_PERCENT +
      PRICING_CONSTANTS.INTEGRATION_SINGLE_TENANT_FIXED;
    baseFee = baseFee + singleTenantAdditional;
  }

  // Apply discount
  if (discountPercent > 0) {
    baseFee = baseFee * (1 - discountPercent / 100);
  }

  return Math.round(baseFee * 100) / 100;
};

/**
 * Calculate total integration setup fees for all selected integrations
 *
 * @param integrationKeys - Array of integration keys
 * @param userCount - Number of users
 * @param isRenewal - Whether this is a renewal
 * @param isMigration - Whether this is a migration
 * @param discountPercent - Discount percentage for setup fees
 * @param isSingleTenant - Whether this is a single tenant deployment (default: false)
 * @returns Total setup fees
 */
export const calculateTotalIntegrationSetupFees = (
  integrationKeys: string[],
  userCount: number,
  isRenewal: boolean,
  isMigration: boolean = false,
  discountPercent: number = 0,
  isSingleTenant: boolean = false,
): number => {
  if (integrationKeys.length === 0) return 0;

  const total = integrationKeys.reduce((sum, key) => {
    // Check if this is a first-time integration (not an existing integration)
    const metadata = INTEGRATION_METADATA[key];
    const isFirstTime = metadata ? !metadata.existing_integration : false;

    const fee = calculateIntegrationSetupFee(
      key,
      userCount,
      isRenewal,
      isMigration,
      discountPercent,
      isSingleTenant,
      isFirstTime,
    );
    return sum + fee;
  }, 0);

  return Math.round(total * 100) / 100;
};

/**
 * Calculate total integration recurring fees for all selected integrations
 *
 * @param integrationKeys - Array of integration keys
 * @param userCount - Number of users
 * @param isGovCloud - Whether this is a gov cloud deployment
 * @param isSingleTenant - Whether this is a single tenant deployment
 * @param is4xDataTransfer - Whether 4x data transfer is enabled
 * @param discountPercent - Discount percentage for recurring fees
 * @returns Total recurring annual fees
 */
export const calculateTotalIntegrationRecurringFees = (
  integrationKeys: string[],
  userCount: number,
  isGovCloud: boolean = false,
  isSingleTenant: boolean = false,
  is4xDataTransfer: boolean = false,
  discountPercent: number = 0,
): number => {
  if (integrationKeys.length === 0) return 0;

  const total = integrationKeys.reduce((sum, key) => {
    const fee = calculateIntegrationRecurringFee(
      key,
      userCount,
      isGovCloud,
      isSingleTenant,
      is4xDataTransfer,
      discountPercent,
    );
    return sum + fee;
  }, 0);

  return Math.round(total * 100) / 100;
};

/**
 * Calculate data transfer per month based on user count
 * Uses the same tier boundaries as other pricing calculations
 *
 * @param userCount - Number of users
 * @returns Data transfer per month
 */
export const calculateDataTransferPerMonth = (userCount: number): number => {
  // Use the predefined tiers
  for (const tier of PRICING_CONSTANTS.SINGLE_TENANT_TIERS) {
    if (userCount >= tier.min && userCount <= tier.max) {
      return tier.dataTransfer;
    }
  }

  // For users above the highest tier (301+), continue the pattern
  // Each 50-user increment adds 50,000 to data transfer
  const lastTier =
    PRICING_CONSTANTS.SINGLE_TENANT_TIERS[PRICING_CONSTANTS.SINGLE_TENANT_TIERS.length - 1];
  const usersAboveLastTier = userCount - lastTier.max;
  const additionalIncrements = Math.ceil(usersAboveLastTier / 50);
  return lastTier.dataTransfer + additionalIncrements * 50000;
};

/** Integration complexity group; Gov Cloud pricing supports 1-5. */
export type ComplexityGroup = 1 | 2 | 3 | 4 | 5;

/** Integration complexity group in the fee tables that stop at 4. */
export type ComplexityGroupUpToFour = 1 | 2 | 3 | 4;

/**
 * Integration metadata including complexity groups and merge support
 */
export interface IntegrationMetadata {
  value: string;
  complexity_group: ComplexityGroup;
  is_on_gov_cloud: boolean;
  existing_integration: boolean;
  supported_by_merge_dev: boolean;
}

/**
 * Integration metadata lookup table
 * Based on Integration table data provided
 */
export const INTEGRATION_METADATA: Record<string, IntegrationMetadata> = {
  'open-api': {
    value: 'api-keys',
    complexity_group: 1,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'google-calendar': {
    value: 'google-calendar',
    complexity_group: 1,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'ms-calendar': {
    value: 'ms-calendar',
    complexity_group: 1,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  workday: {
    value: 'workday',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  ukg: {
    value: 'ukg',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'deltek-vision-cloud': {
    value: 'deltek-vision-cloud',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'deltek-vantagepoint-cloud': {
    value: 'deltek-vantagepoint-cloud',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'deltek-ajera-cloud': {
    value: 'deltek-ajera-cloud',
    complexity_group: 1,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  salesforce: {
    value: 'salesforce',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  'quickbooks-online': {
    value: 'quickbooks-online',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  'quickbooks-desktop': {
    value: 'quickbooks-desktop',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  'jira-cloud': {
    value: 'jira-cloud',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  github: {
    value: 'github',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  asana: {
    value: 'asana',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  'deltek-vision-on-prem': {
    value: 'deltek-vision-hosted',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'deltek-ajera-on-prem': {
    value: 'deltek-ajera-hosted',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'deltek-vantagepoint-on-prem': {
    value: 'deltek-vantagepoint-hosted',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'deltek-ajera-cloud-odbc': {
    value: 'deltek-ajera-cloud-odbc',
    complexity_group: 2,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'unanet-ae': {
    value: 'unanet-ae',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'viewpoint-vista-on-prem': {
    value: 'viewpoint-vista-hosted',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'jira-on-prem': {
    value: 'jira-hosted',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'ms-project-online': {
    value: 'ms-project-cloud',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'bst-global-cloud': {
    value: 'bst-cloud',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'bst-global-sql': {
    value: 'bst-hosted',
    complexity_group: 3,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'deltek-ajera-on-prem-sql': {
    value: 'deltek-ajera-hosted-sql',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'dynamic365-business-central': {
    value: 'ms-business-central',
    complexity_group: 4,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
  'dynamic365-project-operations': {
    value: 'ms-project-operations',
    complexity_group: 4,
    is_on_gov_cloud: true,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'new-custom-api': {
    value: 'custom-api',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'ms-teams': {
    value: 'ms-teams',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  slack: {
    value: 'slack',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'adp-run': {
    value: 'adp-run',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'adp-workforce': {
    value: 'adp-workforce',
    complexity_group: 1,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  bigtime: {
    value: 'bigtime',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  hubspot: {
    value: 'hubspot',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  azuredevops: {
    value: 'azuredevops',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  gitlab: {
    value: 'gitlab',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  xero: {
    value: 'xero',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'sage-intacct': {
    value: 'sage-intacct',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  freshbooks: {
    value: 'freshbooks',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  replicon: {
    value: 'replicon',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'harvest-timesheet': {
    value: 'harvest-timesheet',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  workamajig: {
    value: 'workamajig',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  paylocity: {
    value: 'paylocity',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'product-board': {
    value: 'product-board',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  procore: {
    value: 'procore',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'bamboo-hr': {
    value: 'bamboo-hr',
    complexity_group: 3,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  quickbase: {
    value: 'quickbase',
    complexity_group: 3,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'netsuite-cloud': {
    value: 'netsuite-cloud',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'deltek-pim': {
    value: 'deltek-pim-hosted',
    complexity_group: 3,
    is_on_gov_cloud: false,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'jira-server': {
    value: 'jira-server',
    complexity_group: 3,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'primavera-cloud': {
    value: 'primavera-cloud',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'oracle-cloud': {
    value: 'oracle-cloud',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'financial-force': {
    value: 'financial-force',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'viewpoint-vista-cloud': {
    value: 'viewpoint-vista-cloud',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  cmix: {
    value: 'cmic',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  unanet: {
    value: 'unanet',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'primavera-on-prem': {
    value: 'primavera-hosted',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'oracle-on-prem': {
    value: 'oracle-hosted',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'oracle-hyperion': {
    value: 'oracle-hyperion',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'oracle-fusion': {
    value: 'oracle-fusion',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: true,
  },
  'sap-on-prem': {
    value: 'sap-hosted',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'dynamic365-on-prem': {
    value: 'ms-dynamics-hosted',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  costpoint: {
    value: 'deltek-costpoint-odbc',
    complexity_group: 3,
    is_on_gov_cloud: false,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'monday-com': {
    value: 'monday',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  airtable: {
    value: 'airtable',
    complexity_group: 4,
    is_on_gov_cloud: false,
    existing_integration: false,
    supported_by_merge_dev: false,
  },
  'bqe-core': {
    value: 'bqe-core',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: true,
    supported_by_merge_dev: false,
  },
  'merge-integration': {
    value: 'merge-integration',
    complexity_group: 2,
    is_on_gov_cloud: false,
    existing_integration: true,
    supported_by_merge_dev: true,
  },
};

/**
 * Regular fee pricing table by complexity (1-4) and user count
 * Recurring annual fee
 */
export const INTEGRATION_REGULAR_FEE_TABLE: Record<
  string,
  Record<ComplexityGroupUpToFour, number>
> = {
  '0-25': { 1: 810, 2: 2632, 3: 3528, 4: 6008 },
  '26-51': { 1: 1080, 2: 3510, 3: 4410, 4: 8010 },
  '52-101': { 1: 1620, 2: 5265, 3: 6615, 4: 12015 },
  '102-151': { 1: 2160, 2: 7020, 3: 8820, 4: 16020 },
  '152-201': { 1: 2700, 2: 8775, 3: 11025, 4: 20025 },
  '202-251': { 1: 3240, 2: 10530, 3: 13230, 4: 24030 },
  '252-301': { 1: 3780, 2: 12285, 3: 15435, 4: 28035 },
  '302-351': { 1: 4320, 2: 14040, 3: 17640, 4: 32040 },
  '352-401': { 1: 4860, 2: 15795, 3: 19845, 4: 36045 },
  '402-451': { 1: 5400, 2: 17550, 3: 22050, 4: 40050 },
  '452-501': { 1: 5940, 2: 19305, 3: 24255, 4: 44055 },
  '502-601': { 1: 6750, 2: 21938, 3: 27562, 4: 50062 },
  '602-701': { 1: 7560, 2: 24570, 3: 30870, 4: 56070 },
  '702-801': { 1: 8370, 2: 27202, 3: 34178, 4: 62078 },
  '802-901': { 1: 9180, 2: 29835, 3: 37485, 4: 68085 },
  '902-1001': { 1: 9990, 2: 32468, 3: 40792, 4: 74092 },
  '1002-1251': { 1: 11070, 2: 35978, 3: 45202, 4: 82102 },
  '1252-1501': { 1: 12150, 2: 39488, 3: 49612, 4: 90112 },
  '1502-1751': { 1: 13230, 2: 42998, 3: 54022, 4: 98122 },
  '1752-2001': { 1: 14310, 2: 46508, 3: 58432, 4: 106132 },
  '2002-2251': { 1: 15390, 2: 50018, 3: 62842, 4: 114142 },
  '2252-2501': { 1: 16470, 2: 53528, 3: 67252, 4: 122152 },
  '2502-2751': { 1: 17550, 2: 57038, 3: 71662, 4: 130162 },
  '2752-3001': { 1: 18630, 2: 60548, 3: 76072, 4: 138172 },
  '3002-3251': { 1: 19710, 2: 64058, 3: 80482, 4: 146182 },
  '3252-3501': { 1: 20790, 2: 67568, 3: 84892, 4: 154192 },
};

/**
 * Gov Cloud Platform Fee lookup table by user count tier
 * Annual platform fee for Gov Cloud environments
 *
 * Formula:
 * - Tier 1-49: Base price ($4,500)
 * - Tiers 50-99 to 450-499: previous tier + 0.5 × (tier 1-49 value) = +$2,250
 * - Tiers 500-599 to 900-999: previous tier + 0.5 × (tier 50-99 value) = +$3,375
 * - Tiers 1000-1249 to 3250-3499: previous tier + 0.5 × (tier 100-149 value) = +$4,500
 */
export const GOV_CLOUD_PLATFORM_FEE_TABLE: Record<string, number> = {
  '1-49': 4500, // Base
  '50-99': 6750, // 4500 + 2250
  '100-149': 9000, // 6750 + 2250
  '150-199': 11250, // 9000 + 2250
  '200-249': 13500, // 11250 + 2250
  '250-299': 15750, // 13500 + 2250
  '300-349': 18000, // 15750 + 2250
  '350-399': 20250, // 18000 + 2250
  '400-449': 22500, // 20250 + 2250
  '450-499': 24750, // 22500 + 2250
  '500-599': 28125, // 24750 + 3375 (increment changes to 0.5 × 6750)
  '600-699': 31500, // 28125 + 3375
  '700-799': 34875, // 31500 + 3375
  '800-899': 38250, // 34875 + 3375
  '900-999': 41625, // 38250 + 3375
  '1000-1249': 46125, // 41625 + 4500 (increment changes to 0.5 × 9000)
  '1250-1499': 50625, // 46125 + 4500
  '1500-1749': 55125, // 50625 + 4500
  '1750-1999': 59625, // 55125 + 4500
  '2000-2249': 64125, // 59625 + 4500
  '2250-2499': 68625, // 64125 + 4500
  '2500-2749': 73125, // 68625 + 4500
  '2750-2999': 77625, // 73125 + 4500
  '3000-3249': 82125, // 77625 + 4500
  '3250-3499': 86625, // 82125 + 4500
};

/**
 * Gov Cloud Integration fee pricing table by complexity (1-5) and user count
 * Recurring annual fee for Gov Cloud environments
 * Uses Gov Cloud-specific user tiers (49, 99, 149, etc.)
 *
 * Formula:
 * - Tier 1-49: Base prices per complexity { 1: 1500, 2: 6000, 3: 8000, 4: 12000, 5: 14000 }
 * - Tiers 50-99 to 450-499: previous tier + 0.5 × (tier 1-49 value)
 * - Tiers 500-599 to 900-999: previous tier + 0.5 × (tier 50-99 value)
 * - Tiers 1000-1249 to 3250-3499: previous tier + 0.5 × (tier 100-149 value)
 */
export const GOV_CLOUD_INTEGRATION_FEE_TABLE: Record<string, Record<ComplexityGroup, number>> = {
  // Base tier: { 1: 1500, 2: 6000, 3: 8000, 4: 12000, 5: 14000 }
  '1-49': { 1: 1500, 2: 6000, 3: 8000, 4: 12000, 5: 14000 },
  // Tiers 50-499: increment = 0.5 × base = { 1: 750, 2: 3000, 3: 4000, 4: 6000, 5: 7000 }
  '50-99': { 1: 2250, 2: 9000, 3: 12000, 4: 18000, 5: 21000 },
  '100-149': { 1: 3000, 2: 12000, 3: 16000, 4: 24000, 5: 28000 },
  '150-199': { 1: 3750, 2: 15000, 3: 20000, 4: 30000, 5: 35000 },
  '200-249': { 1: 4500, 2: 18000, 3: 24000, 4: 36000, 5: 42000 },
  '250-299': { 1: 5250, 2: 21000, 3: 28000, 4: 42000, 5: 49000 },
  '300-349': { 1: 6000, 2: 24000, 3: 32000, 4: 48000, 5: 56000 },
  '350-399': { 1: 6750, 2: 27000, 3: 36000, 4: 54000, 5: 63000 },
  '400-449': { 1: 7500, 2: 30000, 3: 40000, 4: 60000, 5: 70000 },
  '450-499': { 1: 8250, 2: 33000, 3: 44000, 4: 66000, 5: 77000 },
  // Tiers 500-999: increment = 0.5 × tier50-99 = { 1: 1125, 2: 4500, 3: 6000, 4: 9000, 5: 10500 }
  '500-599': { 1: 9375, 2: 37500, 3: 50000, 4: 75000, 5: 87500 },
  '600-699': { 1: 10500, 2: 42000, 3: 56000, 4: 84000, 5: 98000 },
  '700-799': { 1: 11625, 2: 46500, 3: 62000, 4: 93000, 5: 108500 },
  '800-899': { 1: 12750, 2: 51000, 3: 68000, 4: 102000, 5: 119000 },
  '900-999': { 1: 13875, 2: 55500, 3: 74000, 4: 111000, 5: 129500 },
  // Tiers 1000-3499: increment = 0.5 × tier100-149 = { 1: 1500, 2: 6000, 3: 8000, 4: 12000, 5: 14000 }
  '1000-1249': { 1: 15375, 2: 61500, 3: 82000, 4: 123000, 5: 143500 },
  '1250-1499': { 1: 16875, 2: 67500, 3: 90000, 4: 135000, 5: 157500 },
  '1500-1749': { 1: 18375, 2: 73500, 3: 98000, 4: 147000, 5: 171500 },
  '1750-1999': { 1: 19875, 2: 79500, 3: 106000, 4: 159000, 5: 185500 },
  '2000-2249': { 1: 21375, 2: 85500, 3: 114000, 4: 171000, 5: 199500 },
  '2250-2499': { 1: 22875, 2: 91500, 3: 122000, 4: 183000, 5: 213500 },
  '2500-2749': { 1: 24375, 2: 97500, 3: 130000, 4: 195000, 5: 227500 },
  '2750-2999': { 1: 25875, 2: 103500, 3: 138000, 4: 207000, 5: 241500 },
  '3000-3249': { 1: 27375, 2: 109500, 3: 146000, 4: 219000, 5: 255500 },
  '3250-3499': { 1: 28875, 2: 115500, 3: 154000, 4: 231000, 5: 269500 },
};

/**
 * Legacy Gov Cloud fee pricing table by complexity (1-4) and user count
 * Kept for backwards compatibility with existing regular pricing tiers
 * @deprecated Use GOV_CLOUD_INTEGRATION_FEE_TABLE with Gov Cloud tiers instead
 */
export const INTEGRATION_GOV_CLOUD_FEE_TABLE: Record<
  string,
  Record<ComplexityGroupUpToFour, number>
> = {
  '0-25': { 1: 1458, 2: 4738, 3: 6350, 4: 10814 },
  '26-51': { 1: 1944, 2: 6318, 3: 7938, 4: 14418 },
  '52-101': { 1: 2916, 2: 9477, 3: 11907, 4: 21627 },
  '102-151': { 1: 3888, 2: 12636, 3: 15876, 4: 28836 },
  '152-201': { 1: 4860, 2: 15795, 3: 19845, 4: 36045 },
  '202-251': { 1: 5832, 2: 18954, 3: 23814, 4: 43254 },
  '252-301': { 1: 6804, 2: 22113, 3: 27783, 4: 50463 },
  '302-351': { 1: 7776, 2: 25272, 3: 31752, 4: 57672 },
  '352-401': { 1: 8748, 2: 28431, 3: 35721, 4: 64881 },
  '402-451': { 1: 9720, 2: 31590, 3: 39690, 4: 72090 },
  '452-501': { 1: 10692, 2: 34749, 3: 43659, 4: 79299 },
  '502-601': { 1: 12150, 2: 39488, 3: 49612, 4: 90112 },
  '602-701': { 1: 13608, 2: 44228, 3: 55566, 4: 100926 },
  '702-801': { 1: 15066, 2: 48964, 3: 61520, 4: 111740 },
  '802-901': { 1: 16524, 2: 53703, 3: 67473, 4: 122553 },
  '902-1001': { 1: 17982, 2: 58442, 3: 73426, 4: 133366 },
  '1002-1251': { 1: 19926, 2: 64760, 3: 81364, 4: 147784 },
  '1252-1501': { 1: 21870, 2: 71078, 3: 89302, 4: 162202 },
  '1502-1751': { 1: 23814, 2: 77396, 3: 97240, 4: 176620 },
  '1752-2001': { 1: 25758, 2: 83714, 3: 105178, 4: 191038 },
  '2002-2251': { 1: 27702, 2: 90032, 3: 113116, 4: 205456 },
  '2252-2501': { 1: 29646, 2: 96350, 3: 121054, 4: 219874 },
  '2502-2751': { 1: 31590, 2: 102668, 3: 128992, 4: 234292 },
  '2752-3001': { 1: 33534, 2: 108986, 3: 136930, 4: 248710 },
  '3002-3251': { 1: 35478, 2: 115304, 3: 144868, 4: 263128 },
  '3252-3501': { 1: 37422, 2: 121622, 3: 152806, 4: 277546 },
};

/**
 * Setup fee pricing based on user count and merge support
 * Used for first-time integrations (new integrations not yet in existing_integration)
 * For existing integrations, use calculateIntegrationSetupFeeByUserCount instead
 */
export const INTEGRATION_SETUP_FEE_TIERS = {
  NOT_SUPPORTED_BY_MERGE: {
    '0-499': 30000,
    '500-999': 35000,
    '1000+': 45000,
  },
  SUPPORTED_BY_MERGE: {
    '0-499': 15000,
    '500-999': 17000,
    '1000+': 22000,
  },
};

/**
 * Calculate integration setup fee based on user count and single tenant status
 *
 * Formula (GLX-1736): both branches scale linearly — every additional 25 users
 * above 100 adds a flat increment. The previous compounding ×1.05 model produced
 * runaway numbers at high seat counts (e.g. $110K at 1,800 users) which were not
 * intended. The increment rate was further softened from 5% → 2% of base after
 * Sales review of typical large-deal pricing.
 *
 * Regular: $4,000 base, +$80 (2% of base) per additional 25 users
 *   - 100 users: $4,000   | 125: $4,080   | 500: $5,280   | 1,800: $9,440
 *
 * Single tenant: $10,000 base, +$250 per additional 25 users (unchanged)
 *   - 100 users: $10,000  | 125: $10,250  | 500: $14,000
 *
 * @param userCount - Number of users
 * @param isSingleTenant - Whether this is a single tenant deployment
 * @returns Setup fee based on user count (rounded to nearest whole number)
 */
export const calculateIntegrationSetupFeeByUserCount = (
  userCount: number,
  isSingleTenant: boolean = false,
): number => {
  const BASE_FEE = isSingleTenant ? 10000 : 4000;
  const ADDITIONAL_FEE_PER_25_USERS = isSingleTenant ? 250 : 80;

  if (userCount <= 100) {
    return BASE_FEE;
  }

  // Number of 25-user blocks above 100 (ceil so e.g. 101 users still counts as one block)
  const usersAbove100 = userCount - 100;
  const additional25UserBlocks = Math.ceil(usersAbove100 / 25);

  return BASE_FEE + additional25UserBlocks * ADDITIONAL_FEE_PER_25_USERS;
};

/**
 * Special override prices for specific integrations
 */
export const INTEGRATION_SPECIAL_SETUP_FEES: Record<string, number> = {
  'api-keys': 0,
  'merge-integration': 7500,
  'bqe-core': 5000,
};

export const PRICING_CONSTANTS = {
  // Additional user costs (monthly)
  INTERNAL_CONTRACTOR_COST: 12.99,
  GUEST_COST: 3.99,
  EXTERNAL_CONTRACTOR_COST: 4.99,

  // Onboarding
  ONBOARDING_FEE_PER_GROUP: 6000.0,
  ONBOARDING_FEE_PER_GROUP_ENTERPRISE: 10000, // GLX-1748: Mosaic Enterprise Plan onboarding rate
  ONBOARDING_HOURS_PER_GROUP: 25, // GLX-1750: implementation hours included per onboarding group
  MEMBERS_PER_ONBOARDING_GROUP: 250, // GLX-1748: group capacity raised 200 -> 250
  ONBOARDING_GROUP_THRESHOLD: 150, // Threshold for adding one more group

  // Enterprise Support
  ENTERPRISE_SUPPORT_HOUR_COST: 200, // $200 per hour for additional Enterprise support

  // Integration data transfer costs
  DEFAULT_DATA_TRANSFER_FREQUENCY: 1, // 1 time per month (free)
  INTEGRATION_FETCH_COST_2X: 200, // 2 times = $200/month
  INTEGRATION_FETCH_COST_3X: 300, // 3 times = $300/month (max)
  INTEGRATION_FETCH_COST_4X_MULTIPLIER: 1.3, // 4x data transfer: multiply base fee by 1.3

  // Integration pricing modifiers
  INTEGRATION_MIGRATION_DISCOUNT: 0.5, // 50% discount on setup fee if migration
  INTEGRATION_SINGLE_TENANT_PERCENT: 0.2, // 20% of original fee
  INTEGRATION_SINGLE_TENANT_FIXED: 10000, // Additional $10,000

  // Single Tenant pricing
  SINGLE_TENANT_DATA_TRANSFER_BASE: 35000, // for tier 1
  SINGLE_TENANT_TIERS: [
    { min: 0, max: 25, dataTransfer: 35000, tier: 1 },
    { min: 26, max: 51, dataTransfer: 51000, tier: 2 },
    { min: 52, max: 101, dataTransfer: 101000, tier: 3 },
    { min: 102, max: 151, dataTransfer: 151000, tier: 4 },
    { min: 152, max: 201, dataTransfer: 201000, tier: 5 },
    { min: 202, max: 251, dataTransfer: 251000, tier: 6 },
    { min: 252, max: 301, dataTransfer: 301000, tier: 7 },
  ],

  // Direct Database Integration pricing
  DIRECT_DATABASE_MIN_FEE: 2000, // Minimum $2,000
  DIRECT_DATABASE_COST_PER_USER: 50, // $50 per user

  // Isolated Database Migration (GLX-1747): 8 hours @ $250/hr, ~1-day downtime
  ISOLATED_DATABASE_MIGRATION_FEE: 2000, // 1-time fee: 8 hours @ $250/hr

  // Isolated AI
  ISOLATED_AI_FEE: 10000, // 1-time fee: $10,000

  // Integration setup hours (GLX-1749) — flat lookup by team size, NOT derived
  // from the setup fee. Per the 5/28 Galaxy pricing review: 5 hours for teams
  // under 200 members, 10 hours for teams of 200 or more.
  INTEGRATION_SETUP_HOURS_MEMBER_THRESHOLD: 200,
  INTEGRATION_SETUP_HOURS_SMALL: 5,
  INTEGRATION_SETUP_HOURS_LARGE: 10,
} as const;

/**
 * Integration setup hours for a single integration (GLX-1749).
 *
 * Flat lookup by team size — deliberately NOT derived from the dollar setup
 * fee. Teams under 200 members get 5 hours; teams of 200 or more get 10 hours.
 *
 * @param memberCount - Number of members
 * @returns Setup hours (5 or 10)
 */
export const calculateIntegrationSetupHours = (memberCount: number): number =>
  memberCount < PRICING_CONSTANTS.INTEGRATION_SETUP_HOURS_MEMBER_THRESHOLD
    ? PRICING_CONSTANTS.INTEGRATION_SETUP_HOURS_SMALL
    : PRICING_CONSTANTS.INTEGRATION_SETUP_HOURS_LARGE;

/**
 * Pricing Preset Keys
 */
export type PricingPresetKey = 'STANDARD' | 'ENTERPRISE' | 'GOV_CLOUD' | 'CUSTOM';

/**
 * Preset configurations for common pricing scenarios.
 * Used by sales reps to quickly set up quotes.
 */
export const PRICING_PRESETS: Record<PricingPresetKey, Partial<PricingCalculatorFormValues>> = {
  /**
   * Standard preset - Most common setup for small/medium businesses
   * 50 members, $14.99/member, no add-ons
   */
  STANDARD: {
    companyName: '',
    subscriptionDuration: 12,
    isRenewal: false,
    isItCustomer: false,
    planType: PlanType.STANDARD,
    numberOfMembers: 50,
    pricePerMember: 14.99,
    discountOnMemberPrice: 0,
    numberOfInternalContractors: 0,
    numberOfGuests: 0,
    numberOfExternalContractors: 0,
    discountOnPlatformFee: 0,
    numberOfOnboardingGroups: 1,
    discountOnOnboardingFee: 0,
    additionalEnterpriseSupportHours: 0,
    directDatabaseIntegration: false,
    discountOnDirectDatabase: 0,
    isolatedDatabase: false,
    discountOnIsolatedDatabase: 0,
    isolatedDatabaseMigration: false,
    tenantType: TenantType.SHARED,
    discountOnTenant: 0,
    isolatedAi: false,
    securityPackageSso: false,
    discountOnSecurityPackageSso: 0,
    govCloud: false,
    govCloudFee: 0,
    discountOnGovCloudFee: 0,
    aiForecasting: false,
    discountOnAiForecasting: 0,
    nonPeopleResources: false,
    discountOnNonPeopleResources: 0,
    profileBuilding: false,
    discountOnProfileBuilding: 0,
    skillsTagging: false,
    discountOnSkillsTagging: 0,
    solutionsSupport: false,
    solutionsSupportHours: 0,
    enableWhiteLabel: false,
    selectedIntegrations: [],
    bakIntegrationFetchTimes: 1,
    enable4xDataTransfer: false,
    integrationMigration: false,
    discountOnIntegrationSetup: 0,
    discountOnIntegrationRecurring: 0,
  },

  /**
   * Enterprise preset - Large organization setup
   * 200 members, Enterprise plan, SSO, Isolated Database
   */
  ENTERPRISE: {
    companyName: '',
    subscriptionDuration: 12,
    isRenewal: false,
    isItCustomer: false,
    planType: PlanType.MOSAIC_ENTERPRISE,
    numberOfMembers: 200,
    pricePerMember: 22.49, // Enterprise price (14.99 * 1.5)
    discountOnMemberPrice: 0,
    numberOfInternalContractors: 0,
    numberOfGuests: 0,
    numberOfExternalContractors: 0,
    discountOnPlatformFee: 0,
    numberOfOnboardingGroups: 1,
    discountOnOnboardingFee: 0,
    additionalEnterpriseSupportHours: 0,
    directDatabaseIntegration: false,
    discountOnDirectDatabase: 0,
    isolatedDatabase: true,
    discountOnIsolatedDatabase: 0,
    isolatedDatabaseMigration: false,
    tenantType: TenantType.SHARED,
    discountOnTenant: 0,
    isolatedAi: false,
    securityPackageSso: true,
    discountOnSecurityPackageSso: 0,
    govCloud: false,
    govCloudFee: 0,
    discountOnGovCloudFee: 0,
    aiForecasting: false,
    discountOnAiForecasting: 0,
    nonPeopleResources: false,
    discountOnNonPeopleResources: 0,
    profileBuilding: false,
    discountOnProfileBuilding: 0,
    skillsTagging: false,
    discountOnSkillsTagging: 0,
    solutionsSupport: false,
    solutionsSupportHours: 0,
    enableWhiteLabel: false,
    selectedIntegrations: [],
    bakIntegrationFetchTimes: 1,
    enable4xDataTransfer: false,
    integrationMigration: false,
    discountOnIntegrationSetup: 0,
    discountOnIntegrationRecurring: 0,
  },

  /**
   * Gov Cloud preset - Government clients
   * Enterprise plan, Gov Cloud, Single Tenant, SSO
   */
  GOV_CLOUD: {
    companyName: '',
    subscriptionDuration: 12,
    isRenewal: false,
    isItCustomer: false,
    planType: PlanType.MOSAIC_ENTERPRISE,
    numberOfMembers: 100,
    pricePerMember: 22.49, // Enterprise price (14.99 * 1.5)
    discountOnMemberPrice: 0,
    numberOfInternalContractors: 0,
    numberOfGuests: 0,
    numberOfExternalContractors: 0,
    discountOnPlatformFee: 0,
    numberOfOnboardingGroups: 1,
    discountOnOnboardingFee: 0,
    additionalEnterpriseSupportHours: 0,
    directDatabaseIntegration: false,
    discountOnDirectDatabase: 0,
    isolatedDatabase: false, // Single tenant includes isolated DB
    discountOnIsolatedDatabase: 0,
    isolatedDatabaseMigration: false,
    tenantType: TenantType.SINGLE,
    discountOnTenant: 0,
    isolatedAi: false,
    securityPackageSso: true,
    discountOnSecurityPackageSso: 0,
    govCloud: true,
    govCloudFee: 0,
    discountOnGovCloudFee: 0,
    aiForecasting: false,
    discountOnAiForecasting: 0,
    nonPeopleResources: false,
    discountOnNonPeopleResources: 0,
    profileBuilding: false,
    discountOnProfileBuilding: 0,
    skillsTagging: false,
    discountOnSkillsTagging: 0,
    solutionsSupport: false,
    solutionsSupportHours: 0,
    enableWhiteLabel: false,
    selectedIntegrations: [],
    bakIntegrationFetchTimes: 1,
    enable4xDataTransfer: false,
    integrationMigration: false,
    discountOnIntegrationSetup: 0,
    discountOnIntegrationRecurring: 0,
  },

  /**
   * Custom preset - Start from scratch with minimal defaults
   */
  CUSTOM: {
    companyName: '',
    subscriptionDuration: 12,
    isRenewal: false,
    isItCustomer: false,
    planType: PlanType.STANDARD,
    numberOfMembers: 1,
    pricePerMember: 14.99,
    discountOnMemberPrice: 0,
    numberOfInternalContractors: 0,
    numberOfGuests: 0,
    numberOfExternalContractors: 0,
    discountOnPlatformFee: 0,
    numberOfOnboardingGroups: 1,
    discountOnOnboardingFee: 0,
    additionalEnterpriseSupportHours: 0,
    directDatabaseIntegration: false,
    discountOnDirectDatabase: 0,
    isolatedDatabase: false,
    discountOnIsolatedDatabase: 0,
    isolatedDatabaseMigration: false,
    tenantType: TenantType.SHARED,
    discountOnTenant: 0,
    isolatedAi: false,
    securityPackageSso: false,
    discountOnSecurityPackageSso: 0,
    govCloud: false,
    govCloudFee: 0,
    discountOnGovCloudFee: 0,
    aiForecasting: false,
    discountOnAiForecasting: 0,
    nonPeopleResources: false,
    discountOnNonPeopleResources: 0,
    profileBuilding: false,
    discountOnProfileBuilding: 0,
    skillsTagging: false,
    discountOnSkillsTagging: 0,
    solutionsSupport: false,
    solutionsSupportHours: 0,
    enableWhiteLabel: false,
    selectedIntegrations: [],
    bakIntegrationFetchTimes: 1,
    enable4xDataTransfer: false,
    integrationMigration: false,
    discountOnIntegrationSetup: 0,
    discountOnIntegrationRecurring: 0,
  },
};

/**
 * Per-integration overrides used when computing a pricing summary.
 *
 * The hook layer carries these as separate Records so each row in the
 * Integrations section can carry its own migration flag, discounts, and
 * pipeline-opportunity count. Tests can omit this argument entirely.
 */
export interface ComputePricingSummaryOptions {
  /** Integrations that were on the previous contract (used for renewal setup-fee waiver). */
  originalIntegrations?: string[];
  /** Per-integration migration override. Falls back to formValues.integrationMigration. */
  integrationMigrations?: Record<string, boolean>;
  /** Per-integration setup-fee discount (0–100). */
  integrationSetupDiscounts?: Record<string, number>;
  /** Per-integration recurring-fee discount (0–100). */
  integrationRecurringDiscounts?: Record<string, number>;
  /** Per-integration pipeline-opportunity count for first-time integrations. Defaults to 1. */
  integrationPipelineOpportunities?: Record<string, number>;
}

interface DatabaseAndTenancyFees {
  directDatabaseFeeBeforeDiscount: number;
  directDatabaseFee: number;
  isolatedDatabaseFeeYearlyBeforeDiscount: number;
  isolatedDatabaseFeeYearly: number;
  isolatedDatabaseMigrationApplies: boolean;
  isolatedDatabaseMigrationFee: number;
  isolatedAiFee: number;
}

/**
 * Direct DB / Isolated DB / migration / Isolated AI fees. Dedicated tenancy
 * (Single or Enterprise) supersedes all of them — hence `onDedicatedTenant`.
 */
const computeDatabaseAndTenancyFees = (
  formValues: PricingCalculatorFormValues,
  onDedicatedTenant: boolean,
): DatabaseAndTenancyFees => {
  const directDatabaseFeeBeforeDiscount =
    formValues.directDatabaseIntegration && !onDedicatedTenant
      ? calculateDirectDatabaseFee(formValues.numberOfMembers, formValues.subscriptionDuration, 0)
      : 0;
  const directDatabaseFee =
    formValues.directDatabaseIntegration && !onDedicatedTenant
      ? calculateDirectDatabaseFee(
          formValues.numberOfMembers,
          formValues.subscriptionDuration,
          formValues.discountOnDirectDatabase,
        )
      : 0;

  const isolatedDatabaseFeeMonthly =
    formValues.isolatedDatabase &&
    !formValues.directDatabaseIntegration &&
    !onDedicatedTenant &&
    !formValues.isolatedAi
      ? calculateIsolatedDatabaseFee(formValues.numberOfMembers)
      : 0;
  const isolatedDatabaseFeeYearlyBeforeDiscount =
    isolatedDatabaseFeeMonthly * formValues.subscriptionDuration;
  const isolatedDatabaseFeeYearly =
    isolatedDatabaseFeeYearlyBeforeDiscount * (1 - formValues.discountOnIsolatedDatabase / 100);

  // GLX-1747 (Copilot review): only charge the migration fee for a *standalone*
  // Isolated DB move or Direct DB — not a dedicated tenant (bundles its DB) and
  // not an Isolated-DB that's only on because Isolated AI auto-enabled it
  // (isolatedAi sets isolatedDatabase = true; that DB is bundled, not a
  // standalone migration). Otherwise a stale `isolatedDatabaseMigration` flag
  // could apply a phantom migration fee the UI no longer surfaces a control to clear.
  const isolatedDatabaseMigrationApplies =
    formValues.isolatedDatabaseMigration &&
    ((formValues.isolatedDatabase && !formValues.isolatedAi) ||
      formValues.directDatabaseIntegration) &&
    !onDedicatedTenant;
  const isolatedDatabaseMigrationFee = isolatedDatabaseMigrationApplies
    ? PRICING_CONSTANTS.ISOLATED_DATABASE_MIGRATION_FEE
    : 0;

  const isolatedAiFee =
    formValues.isolatedAi && !onDedicatedTenant ? PRICING_CONSTANTS.ISOLATED_AI_FEE : 0;

  return {
    directDatabaseFeeBeforeDiscount,
    directDatabaseFee,
    isolatedDatabaseFeeYearlyBeforeDiscount,
    isolatedDatabaseFeeYearly,
    isolatedDatabaseMigrationApplies,
    isolatedDatabaseMigrationFee,
    isolatedAiFee,
  };
};

interface IntegrationSetupFeeTotals {
  integrationSetupFee: number;
  integrationSetupFeeAfterDiscount: number;
}

const computeIntegrationSetupFees = (
  formValues: PricingCalculatorFormValues,
  onDedicatedTenant: boolean,
  originalIntegrations: string[] | undefined,
  integrationMigrations: Record<string, boolean>,
  integrationSetupDiscounts: Record<string, number>,
  integrationPipelineOpportunities: Record<string, number>,
): IntegrationSetupFeeTotals => {
  let integrationSetupFee = 0;
  let integrationSetupFeeAfterDiscount = 0;

  formValues.selectedIntegrations.forEach((integrationKey) => {
    // GLX-1746: a baseline contract is "known" only when the caller passed
    // `originalIntegrations` (even as []). An undefined value means no
    // baseline was loaded — e.g. the user just ticked isRenewal without
    // loading a prior contract. In that case we can't tell what's newly
    // added, so we conservatively waive setup on every selected integration
    // for the renewal (matches the bug fix).
    //
    // When a baseline *is* known (originalIntegrations !== undefined,
    // including []), we use it strictly: integrations not in that array are
    // genuinely new and still get charged setup fees on renewal.
    const isNewlyAdded =
      originalIntegrations !== undefined && !originalIntegrations.includes(integrationKey);
    const shouldWaiveSetupFee = formValues.isRenewal && !isNewlyAdded;
    const perIntegrationSetupDiscount = integrationSetupDiscounts[integrationKey] || 0;
    const pipelineOpportunities = integrationPipelineOpportunities[integrationKey] || 1;

    // Use ?? (not ||) so an explicit per-integration `false` override —
    // i.e., user unchecked migration for this row — beats the form-level flag.
    const isMigrationForThisIntegration =
      integrationMigrations[integrationKey] ?? formValues.integrationMigration;

    const baseSetupFee = calculateTotalIntegrationSetupFees(
      [integrationKey],
      formValues.numberOfMembers,
      shouldWaiveSetupFee,
      isMigrationForThisIntegration,
      0,
      onDedicatedTenant,
    );

    const metadata = INTEGRATION_METADATA[integrationKey];
    const isFirstTime = metadata ? !metadata.existing_integration : false;

    let discountedSetupFee: number;
    if (isFirstTime && pipelineOpportunities > 1) {
      const perOpportunityFee = baseSetupFee / pipelineOpportunities;
      const perOpportunityFeeAfterDiscount =
        perOpportunityFee * (1 - perIntegrationSetupDiscount / 100);
      discountedSetupFee = perOpportunityFeeAfterDiscount * pipelineOpportunities;
    } else {
      discountedSetupFee = calculateTotalIntegrationSetupFees(
        [integrationKey],
        formValues.numberOfMembers,
        shouldWaiveSetupFee,
        isMigrationForThisIntegration,
        perIntegrationSetupDiscount,
        onDedicatedTenant,
      );
    }

    integrationSetupFee += baseSetupFee;
    integrationSetupFeeAfterDiscount += discountedSetupFee;
  });

  return { integrationSetupFee, integrationSetupFeeAfterDiscount };
};

interface IntegrationRecurringFeeTotals {
  integrationRecurringFee: number;
  integrationRecurringFeeAfterDiscount: number;
}

const computeIntegrationRecurringFees = (
  formValues: PricingCalculatorFormValues,
  onDedicatedTenant: boolean,
  is4xDataTransfer: boolean,
  integrationRecurringDiscounts: Record<string, number>,
): IntegrationRecurringFeeTotals => {
  let integrationRecurringFee = 0;
  let integrationRecurringFeeAfterDiscount = 0;

  formValues.selectedIntegrations.forEach((integrationKey) => {
    const perIntegrationRecurringDiscount = integrationRecurringDiscounts[integrationKey] || 0;

    const baseRecurringFee = calculateTotalIntegrationRecurringFees(
      [integrationKey],
      formValues.numberOfMembers,
      formValues.govCloud,
      onDedicatedTenant,
      is4xDataTransfer,
      0,
    );

    const discountedRecurringFee = calculateTotalIntegrationRecurringFees(
      [integrationKey],
      formValues.numberOfMembers,
      formValues.govCloud,
      onDedicatedTenant,
      is4xDataTransfer,
      perIntegrationRecurringDiscount,
    );

    integrationRecurringFee += baseRecurringFee;
    integrationRecurringFeeAfterDiscount += discountedRecurringFee;
  });

  return { integrationRecurringFee, integrationRecurringFeeAfterDiscount };
};

const computeAdditionalBakIntegrationFetchingFee = (
  formValues: PricingCalculatorFormValues,
): number =>
  formValues.bakIntegrationFetchTimes === 2
    ? PRICING_CONSTANTS.INTEGRATION_FETCH_COST_2X * formValues.subscriptionDuration
    : formValues.bakIntegrationFetchTimes === 3
      ? PRICING_CONSTANTS.INTEGRATION_FETCH_COST_3X * formValues.subscriptionDuration
      : 0;

/**
 * Gov Cloud surcharge: 20% of member cost + SSO + database + integration
 * recurring, all priced at REGULAR rates so the surcharge isn't compounded.
 */
const computeGovCloudFeeTotal = (
  formValues: PricingCalculatorFormValues,
  onDedicatedTenant: boolean,
  is4xDataTransfer: boolean,
  integrationRecurringDiscounts: Record<string, number>,
): number => {
  if (!formValues.govCloud) return 0;

  const memberCost =
    formValues.numberOfMembers *
    formValues.pricePerMember *
    (1 - formValues.discountOnMemberPrice / 100) *
    formValues.subscriptionDuration;

  const ssoFeePerUserForGov = formValues.securityPackageSso
    ? calculateSsoFeePerUser(formValues.numberOfMembers)
    : 0;
  const ssoTotalCost =
    ssoFeePerUserForGov * formValues.numberOfMembers * formValues.subscriptionDuration;

  let databaseFee = 0;
  if (onDedicatedTenant) {
    databaseFee = calculateTenantFee(
      formValues.tenantType,
      formValues.numberOfMembers,
      formValues.subscriptionDuration,
    );
  } else if (formValues.isolatedDatabase) {
    databaseFee =
      calculateIsolatedDatabaseFee(formValues.numberOfMembers) * formValues.subscriptionDuration;
  }

  let totalIntegrationFee = 0;
  formValues.selectedIntegrations.forEach((integrationKey) => {
    const perIntegrationRecurringDiscount = integrationRecurringDiscounts[integrationKey] || 0;
    // Use REGULAR prices (not Gov Cloud) so the 20% surcharge isn't applied on top of itself.
    const regularFee = calculateTotalIntegrationRecurringFees(
      [integrationKey],
      formValues.numberOfMembers,
      false,
      onDedicatedTenant,
      is4xDataTransfer,
      perIntegrationRecurringDiscount,
    );
    totalIntegrationFee += regularFee;
  });

  return 0.2 * (memberCost + ssoTotalCost + databaseFee + totalIntegrationFee);
};

interface AddOnFees {
  aiForecastingFeeBeforeDiscount: number;
  aiForecastingFeeAfterDiscount: number;
  nonPeopleResourcesFeeBeforeDiscount: number;
  nonPeopleResourcesFeeAfterDiscount: number;
  profileBuildingFeeBeforeDiscount: number;
  profileBuildingFeeAfterDiscount: number;
  skillsTaggingFeeBeforeDiscount: number;
  skillsTaggingFeeAfterDiscount: number;
  solutionsSupportFee: number;
  whiteLabelAdditionalCost: number;
}

const computeAddOnFees = (
  formValues: PricingCalculatorFormValues,
  integrationTierFeeAnnual: number,
): AddOnFees => {
  const aiForecastingFeeBeforeDiscount = formValues.aiForecasting
    ? calculateAiForecastingFee(
        formValues.numberOfMembers,
        formValues.pricePerMember,
        formValues.discountOnMemberPrice,
        formValues.subscriptionDuration,
        integrationTierFeeAnnual,
      )
    : 0;
  const aiForecastingFeeAfterDiscount =
    aiForecastingFeeBeforeDiscount * (1 - formValues.discountOnAiForecasting / 100);

  const nonPeopleResourcesFeeBeforeDiscount = formValues.nonPeopleResources
    ? calculateNonPeopleResourcesFee(
        formValues.numberOfMembers,
        formValues.pricePerMember,
        formValues.discountOnMemberPrice,
        formValues.subscriptionDuration,
      )
    : 0;
  const nonPeopleResourcesFeeAfterDiscount =
    nonPeopleResourcesFeeBeforeDiscount * (1 - formValues.discountOnNonPeopleResources / 100);

  const profileBuildingFeeBeforeDiscount = formValues.profileBuilding
    ? calculateProfileBuildingFee(formValues.numberOfMembers, formValues.subscriptionDuration)
    : 0;
  const profileBuildingFeeAfterDiscount =
    profileBuildingFeeBeforeDiscount * (1 - formValues.discountOnProfileBuilding / 100);

  const skillsTaggingFeeBeforeDiscount = formValues.skillsTagging
    ? calculateSkillsTaggingFee(formValues.numberOfMembers, formValues.subscriptionDuration)
    : 0;
  const skillsTaggingFeeAfterDiscount =
    skillsTaggingFeeBeforeDiscount * (1 - formValues.discountOnSkillsTagging / 100);

  const solutionsSupportFee = formValues.solutionsSupport
    ? calculateSolutionsSupportFee(formValues.solutionsSupportHours)
    : 0;

  const whiteLabelAdditionalCost = formValues.enableWhiteLabel
    ? calculateWhiteLabelAdditionalCost(
        formValues.numberOfMembers,
        formValues.pricePerMember,
        formValues.discountOnMemberPrice,
        formValues.subscriptionDuration,
      )
    : 0;

  return {
    aiForecastingFeeBeforeDiscount,
    aiForecastingFeeAfterDiscount,
    nonPeopleResourcesFeeBeforeDiscount,
    nonPeopleResourcesFeeAfterDiscount,
    profileBuildingFeeBeforeDiscount,
    profileBuildingFeeAfterDiscount,
    skillsTaggingFeeBeforeDiscount,
    skillsTaggingFeeAfterDiscount,
    solutionsSupportFee,
    whiteLabelAdditionalCost,
  };
};

/**
 * Pure function: produce the canonical PricingCalculatorSummary for a given
 * set of form values and per-integration overrides.
 *
 * Both the hook (for live form state) and the unit tests consume this. Keeping
 * it pure and side-effect-free is what lets the validation set assert exact
 * totals against the Notion spec without spinning up React.
 *
 * @see usePricingCalculatorForm — the hook delegates to this
 * @see pricingCalculatorValidationSet.test.ts — fixtures driven from the Notion guide
 */
export const computePricingSummary = (
  formValues: PricingCalculatorFormValues,
  options: ComputePricingSummaryOptions = {},
): PricingCalculatorSummary => {
  const {
    // GLX-1746: don't default to []; preserve undefined so callers can
    // distinguish "no baseline contract loaded" (waive setup on renewal) from
    // "baseline loaded, contract had zero integrations" (still charge setup
    // when a new integration is added on renewal).
    originalIntegrations,
    integrationMigrations = {},
    integrationSetupDiscounts = {},
    integrationRecurringDiscounts = {},
    integrationPipelineOpportunities = {},
  } = options;

  const platformFeeData = calculatePlatformFee(formValues.numberOfMembers, formValues.planType);

  const discountedPricePerMember =
    formValues.pricePerMember * (1 - formValues.discountOnMemberPrice / 100);
  const totalMemberCostMonthly = formValues.numberOfMembers * discountedPricePerMember;
  const totalMemberCostYearly = totalMemberCostMonthly * formValues.subscriptionDuration;

  const internalContractorsTotal =
    formValues.numberOfInternalContractors *
    PRICING_CONSTANTS.INTERNAL_CONTRACTOR_COST *
    formValues.subscriptionDuration;
  const guestsTotal =
    formValues.numberOfGuests * PRICING_CONSTANTS.GUEST_COST * formValues.subscriptionDuration;
  const externalContractorsTotal =
    formValues.numberOfExternalContractors *
    PRICING_CONSTANTS.EXTERNAL_CONTRACTOR_COST *
    formValues.subscriptionDuration;

  const platformFeeAfterDiscount =
    formValues.platformFee * (1 - formValues.discountOnPlatformFee / 100);

  const ssoFeePerUser = formValues.securityPackageSso
    ? calculateSsoFeePerUser(formValues.numberOfMembers)
    : 0;
  const ssoTotalYearlyBeforeDiscount =
    ssoFeePerUser * formValues.numberOfMembers * formValues.subscriptionDuration;
  const ssoTotalYearly =
    ssoTotalYearlyBeforeDiscount * (1 - formValues.discountOnSecurityPackageSso / 100);

  // Dedicated tenancy (Single or Enterprise) supersedes ad-hoc Direct DB / Isolated DB / Isolated AI
  const onDedicatedTenant = isDedicatedTenant(formValues.tenantType);

  const {
    directDatabaseFeeBeforeDiscount,
    directDatabaseFee,
    isolatedDatabaseFeeYearlyBeforeDiscount,
    isolatedDatabaseFeeYearly,
    isolatedDatabaseMigrationApplies,
    isolatedDatabaseMigrationFee,
    isolatedAiFee,
  } = computeDatabaseAndTenancyFees(formValues, onDedicatedTenant);

  const integrationTierFeeAnnual = calculateIntegrationTierPricing(
    formValues.selectedIntegrations.length,
    formValues.numberOfMembers,
  );

  const tenantFeeBeforeDiscount = calculateTenantFee(
    formValues.tenantType,
    formValues.numberOfMembers,
    formValues.subscriptionDuration,
  );
  const tenantFee = tenantFeeBeforeDiscount * (1 - formValues.discountOnTenant / 100);

  const onboardingFeeAfterDiscount = formValues.isRenewal
    ? 0
    : formValues.onboardingFee * (1 - formValues.discountOnOnboardingFee / 100);

  const { integrationSetupFee, integrationSetupFeeAfterDiscount } = computeIntegrationSetupFees(
    formValues,
    onDedicatedTenant,
    originalIntegrations,
    integrationMigrations,
    integrationSetupDiscounts,
    integrationPipelineOpportunities,
  );

  const is4xDataTransfer = formValues.enable4xDataTransfer;

  const { integrationRecurringFee, integrationRecurringFeeAfterDiscount } =
    computeIntegrationRecurringFees(
      formValues,
      onDedicatedTenant,
      is4xDataTransfer,
      integrationRecurringDiscounts,
    );

  const additionalBakIntegrationFetchingFee =
    computeAdditionalBakIntegrationFetchingFee(formValues);

  const additionalEnterpriseSupportCost =
    formValues.additionalEnterpriseSupportHours * PRICING_CONSTANTS.ENTERPRISE_SUPPORT_HOUR_COST;

  const govCloudFeeTotal = computeGovCloudFeeTotal(
    formValues,
    onDedicatedTenant,
    is4xDataTransfer,
    integrationRecurringDiscounts,
  );
  const govCloudFeeAfterDiscount = govCloudFeeTotal * (1 - formValues.discountOnGovCloudFee / 100);

  const {
    aiForecastingFeeBeforeDiscount,
    aiForecastingFeeAfterDiscount,
    nonPeopleResourcesFeeBeforeDiscount,
    nonPeopleResourcesFeeAfterDiscount,
    profileBuildingFeeBeforeDiscount,
    profileBuildingFeeAfterDiscount,
    skillsTaggingFeeBeforeDiscount,
    skillsTaggingFeeAfterDiscount,
    solutionsSupportFee,
    whiteLabelAdditionalCost,
  } = computeAddOnFees(formValues, integrationTierFeeAnnual);

  const oneTimeFeeSubtotal =
    onboardingFeeAfterDiscount +
    integrationSetupFeeAfterDiscount +
    directDatabaseFee +
    isolatedDatabaseMigrationFee +
    isolatedAiFee +
    additionalEnterpriseSupportCost +
    profileBuildingFeeAfterDiscount +
    skillsTaggingFeeAfterDiscount +
    solutionsSupportFee;

  const recurringFeeSubtotal =
    totalMemberCostYearly +
    internalContractorsTotal +
    guestsTotal +
    externalContractorsTotal +
    platformFeeAfterDiscount +
    ssoTotalYearly +
    isolatedDatabaseFeeYearly +
    integrationRecurringFeeAfterDiscount +
    additionalBakIntegrationFetchingFee +
    tenantFee +
    govCloudFeeAfterDiscount +
    aiForecastingFeeAfterDiscount +
    nonPeopleResourcesFeeAfterDiscount +
    whiteLabelAdditionalCost;

  const totalCost = oneTimeFeeSubtotal + recurringFeeSubtotal;

  return {
    originalPricePerMember: formValues.pricePerMember,
    discountedPricePerMember,
    totalMemberCostMonthly,
    totalMemberCostYearly,
    internalContractorsTotal,
    guestsTotal,
    externalContractorsTotal,
    platformFeeMonthly: formValues.platformFee / formValues.subscriptionDuration,
    platformFeeAfterDiscountMonthly: platformFeeAfterDiscount / formValues.subscriptionDuration,
    platformFeeYearly: platformFeeAfterDiscount,
    hoursIncluded: formValues.isItCustomer
      ? roundHoursUp(Math.round(platformFeeData.hoursIncluded / 2))
      : platformFeeData.hoursIncluded,
    additionalEnterpriseSupportHours: formValues.additionalEnterpriseSupportHours,
    additionalEnterpriseSupportCost,
    onboardingFee: formValues.onboardingFee,
    onboardingFeeAfterDiscount,
    isolatedDatabase: formValues.isolatedDatabase,
    directDatabase: formValues.directDatabaseIntegration,
    directDatabaseFeeBeforeDiscount,
    directDatabaseFee,
    isMigration: isolatedDatabaseMigrationApplies,
    oneTimeMaintenanceFee: isolatedDatabaseMigrationFee,
    isolatedDatabaseFeeYearlyBeforeDiscount,
    isolatedDatabaseFeeYearly,
    tenantType: formValues.tenantType,
    tenantFeeBeforeDiscount,
    tenantFee,
    isolatedAi: formValues.isolatedAi,
    isolatedAiFee,
    securityPackageSso: formValues.securityPackageSso,
    ssoTotalYearlyBeforeDiscount,
    ssoTotalYearly,
    govCloud: formValues.govCloud,
    govCloudFee: govCloudFeeTotal,
    govCloudFeeAfterDiscount,
    aiForecasting: formValues.aiForecasting,
    aiForecastingFee: aiForecastingFeeBeforeDiscount,
    aiForecastingFeeAfterDiscount,
    nonPeopleResources: formValues.nonPeopleResources,
    nonPeopleResourcesFee: nonPeopleResourcesFeeBeforeDiscount,
    nonPeopleResourcesFeeAfterDiscount,
    profileBuilding: formValues.profileBuilding,
    profileBuildingFee: profileBuildingFeeBeforeDiscount,
    profileBuildingFeeAfterDiscount,
    skillsTagging: formValues.skillsTagging,
    skillsTaggingFee: skillsTaggingFeeBeforeDiscount,
    skillsTaggingFeeAfterDiscount,
    solutionsSupport: formValues.solutionsSupport,
    solutionsSupportHours: formValues.solutionsSupportHours,
    solutionsSupportFee,
    enableWhiteLabel: formValues.enableWhiteLabel,
    whiteLabelAdditionalCost,
    totalBakIntegrationFetchTimes: formValues.bakIntegrationFetchTimes,
    additionalBakIntegrationFetchingFee,
    integrationSetupFee,
    integrationSetupFeeAfterDiscount,
    integrationRecurringFee,
    integrationRecurringFeeAfterDiscount,
    oneTimeFeeSubtotal,
    recurringFeeSubtotal,
    totalCost,
  };
};

/**
 * Section subtotals shown in the Calculator tab.
 *
 * Both the Form (left column) and the Sticky panel (right column) read this
 * helper, so the same line item can never appear under two different totals.
 * Keep the shape stable — UI components destructure these fields directly.
 */
export interface PricingSectionSubtotals {
  /** Members + contractors + guests (recurring). */
  memberPricingSubtotal: number;
  /** Platform fee + onboarding + extra enterprise support hours. */
  platformSubtotal: number;
  /** Direct DB + Isolated DB + dedicated tenant (Single/Enterprise) + Isolated AI + DB migration. SSO is in add-ons. */
  serverSubtotal: number;
  /** Gov Cloud surcharge (after discount). Surfaced separately when enabled. */
  govCloudSubtotal: number;
  /** SSO + AI Forecasting + NPR + Profile Building + Skills Tagging + Solutions Support + White-Label. */
  addonsSubtotal: number;
  /** Integration setup + recurring + legacy 2x/3x data transfer fee. */
  integrationsSubtotal: number;
}

/**
 * Compute the per-section subtotals shown in the Calculator tab. Form and
 * Sticky panel must consume this — see PR for GLX-1675 where the two views
 * disagreed because each had its own grouping.
 */
export const computeSectionSubtotals = (
  summary: PricingCalculatorSummary,
): PricingSectionSubtotals => ({
  memberPricingSubtotal:
    summary.totalMemberCostYearly +
    summary.internalContractorsTotal +
    summary.guestsTotal +
    summary.externalContractorsTotal,
  platformSubtotal:
    summary.platformFeeYearly +
    summary.onboardingFeeAfterDiscount +
    (summary.additionalEnterpriseSupportCost || 0),
  serverSubtotal:
    summary.directDatabaseFee +
    summary.isolatedDatabaseFeeYearly +
    summary.oneTimeMaintenanceFee +
    summary.tenantFee +
    summary.isolatedAiFee,
  govCloudSubtotal: summary.govCloudFeeAfterDiscount,
  addonsSubtotal:
    summary.ssoTotalYearly +
    summary.aiForecastingFeeAfterDiscount +
    summary.nonPeopleResourcesFeeAfterDiscount +
    summary.profileBuildingFeeAfterDiscount +
    summary.skillsTaggingFeeAfterDiscount +
    summary.solutionsSupportFee +
    summary.whiteLabelAdditionalCost,
  integrationsSubtotal:
    summary.integrationSetupFeeAfterDiscount +
    summary.integrationRecurringFeeAfterDiscount +
    summary.additionalBakIntegrationFetchingFee,
});
